export type CarType = 'Sedán' | 'SUV' | 'Pick-up' | 'Hatchback' | 'Deportivo'

export interface TrimLevel {
  name: string
  priceEstimate: string
  auctionEstimate: [number, number]
  features: string[]
}

export interface CarModel {
  id: string
  make: string
  model: string
  yearRange: string
  auctionRange: { min: number; max: number }
  priceRange: { min: number; max: number }
  type: CarType
  engine: string
  description: string
  trims: TrimLevel[]
}

// ═══════════════════════════════════════
// IAAI LICENSED BUYER FEE SCHEDULE
// ═══════════════════════════════════════
export function getIAAIBuyerFee(bid: number): number {
  if (bid < 100) return 25
  if (bid < 200) return 40
  if (bid < 300) return 60
  if (bid < 400) return 75
  if (bid < 500) return 75
  if (bid < 600) return 85
  if (bid < 700) return 85
  if (bid < 800) return 100
  if (bid < 900) return 125
  if (bid < 1000) return 150
  if (bid < 1200) return 175
  if (bid < 1300) return 200
  if (bid < 1500) return 225
  if (bid < 1600) return 250
  if (bid < 1800) return 275
  if (bid < 2000) return 275
  if (bid < 2400) return 325
  if (bid < 2500) return 350
  if (bid < 3000) return 375
  if (bid < 3500) return 400
  if (bid < 4000) return 425
  if (bid < 4500) return 450
  if (bid < 5000) return 475
  if (bid < 6000) return 525
  if (bid < 7500) return 575
  if (bid < 10000) return 650
  if (bid < 15000) return 750
  if (bid < 20000) return 850
  return 900
}

// ═══════════════════════════════════════
// TOTAL LANDED COST CALCULATION
// Auction Bid → Total cost in El Salvador
// ═══════════════════════════════════════
export function calculateTotalCost(auctionBid: number): number {
  const iaaiFee = getIAAIBuyerFee(auctionBid)
  const transport = 1300 // ground + boat average

  // CIF = auction bid + IAAI fees + transport
  const cif = auctionBid + iaaiFee + transport

  // Taxes (calculated over CIF)
  const seguro = cif * 0.015 // 1.5% insurance
  const dai = cif * 0.25 // 25% import duty
  const iva = cif * 0.1625 // effective 13% on (CIF + DAI + seguro)
  const aiv = cif * 0.01 // 1% additional

  // Fixed fees
  const pe = 40 // Permiso de Entrada
  const inspeccion = 18 // Inspection
  const primeraMatricula = 350 // First registration (average)

  // Commission + IVA
  const comision = 250 * 1.13 // $282.50

  // Post-import costs
  const reparaciones = 1300 // estimated repairs average
  const circulacion = 150 // circulation costs

  const total =
    cif +
    seguro +
    dai +
    iva +
    aiv +
    pe +
    inspeccion +
    primeraMatricula +
    comision +
    reparaciones +
    circulacion

  return Math.round(total / 100) * 100 // round to nearest $100
}

// ═══════════════════════════════════════
// HELPER FUNCTIONS
// ═══════════════════════════════════════
function formatPriceRange(min: number, max: number): string {
  return `$${min.toLocaleString()} - $${max.toLocaleString()}`
}

export function getCarImageUrl(
  make: string,
  model: string,
  year: number,
): string {
  const cleanModel = model
    .replace(/\s*hatchback/i, '')
    .replace(/^3$/, '3')
    .trim()
  return `https://cdn.imagin.studio/getimage?customer=hrjavascript-mastery&make=${encodeURIComponent(make)}&modelFamily=${encodeURIComponent(cleanModel)}&modelYear=${year}&angle=1`
}

export function getYearsFromRange(yearRange: string): number[] {
  const parts = yearRange.split('-').map(Number)
  if (parts.length === 1) return [parts[0]]
  const [start, end] = parts
  const years: number[] = []
  for (let y = start; y <= end; y++) years.push(y)
  return years
}

export function estimatePriceForYear(
  car: CarModel,
  year: number,
): { min: number; max: number } {
  const years = getYearsFromRange(car.yearRange)
  if (years.length <= 1) return car.priceRange
  const oldestYear = years[0]
  const newestYear = years[years.length - 1]
  const totalSpan = newestYear - oldestYear
  if (totalSpan === 0) return car.priceRange

  // Interpolation factor: 0 = oldest year, 1 = newest year
  const factor = (year - oldestYear) / totalSpan

  // Interpolate auction bids by year, then compute total cost
  const auctionSpan = car.auctionRange.max - car.auctionRange.min
  const yearAuctionMin = Math.round(
    car.auctionRange.min + auctionSpan * factor * 0.5,
  )
  const yearAuctionMax = Math.round(
    car.auctionRange.min + auctionSpan * (0.3 + factor * 0.7),
  )

  const min = calculateTotalCost(yearAuctionMin)
  const max = calculateTotalCost(Math.max(yearAuctionMax, yearAuctionMin + 500))

  return { min, max: Math.max(max, min + 500) }
}

// ═══════════════════════════════════════
// CAR DATA BUILDER
// Stores auction bid estimates, computes total costs automatically
// ═══════════════════════════════════════
interface RawTrim {
  name: string
  auction: [number, number]
  features: string[]
}

interface RawCar {
  id: string
  make: string
  model: string
  yearRange: string
  auction: [number, number] // [min bid, max bid]
  type: CarType
  engine: string
  description: string
  trims: RawTrim[]
}

function buildCarData(raw: RawCar[]): CarModel[] {
  return raw.map((car) => ({
    id: car.id,
    make: car.make,
    model: car.model,
    yearRange: car.yearRange,
    auctionRange: { min: car.auction[0], max: car.auction[1] },
    priceRange: {
      min: calculateTotalCost(car.auction[0]),
      max: calculateTotalCost(car.auction[1]),
    },
    type: car.type,
    engine: car.engine,
    description: car.description,
    trims: car.trims.map((t) => ({
      name: t.name,
      auctionEstimate: t.auction as [number, number],
      priceEstimate: formatPriceRange(
        calculateTotalCost(t.auction[0]),
        calculateTotalCost(t.auction[1]),
      ),
      features: t.features,
    })),
  }))
}

// ═══════════════════════════════════════
// RAW CAR DATA (auction bid estimates)
// Prices based on typical IAAI/Copart archived auction results
// for import-grade vehicles (run & drive, various conditions)
// ═══════════════════════════════════════
const rawCarData: RawCar[] = [
  // ═══════════════════════════════════════
  // TOYOTA
  // ═══════════════════════════════════════
  {
    id: 'corolla-11',
    make: 'Toyota',
    model: 'Corolla',
    yearRange: '2018-2019',
    auction: [1500, 3500],
    type: 'Sedán',
    engine: '1.8L 4cyl',
    description:
      'Generación anterior, probada y extremadamente confiable. Ideal para primer auto.',
    trims: [
      {
        name: 'L',
        auction: [1500, 2200],
        features: ['Básico', 'Confiable', 'Tela'],
      },
      {
        name: 'LE',
        auction: [2000, 2800],
        features: ['Cámara reversa', 'Pantalla 6.1"', 'Climatizador'],
      },
      {
        name: 'SE',
        auction: [2500, 3500],
        features: ['Deportivo', 'Rines 17"', 'Paddle Shifters'],
      },
    ],
  },
  {
    id: 'corolla-12',
    make: 'Toyota',
    model: 'Corolla',
    yearRange: '2020-2024',
    auction: [3500, 9000],
    type: 'Sedán',
    engine: '2.0L 4cyl',
    description:
      'Diseño totalmente renovado, más seguro y con mejor tecnología.',
    trims: [
      {
        name: 'L',
        auction: [3500, 5000],
        features: ['Apple CarPlay', 'Toyota Safety Sense 2.0', 'LEDs'],
      },
      {
        name: 'LE',
        auction: [4500, 6500],
        features: ['Pantalla 8"', 'Rines 16"', 'Climatizador auto'],
      },
      {
        name: 'SE',
        auction: [5500, 7500],
        features: ['Motor 2.0L', 'Suspensión deportiva', 'Rines 18"'],
      },
      {
        name: 'XSE',
        auction: [7000, 9000],
        features: ['Cuero', 'Sunroof', 'Asientos calefactados'],
      },
    ],
  },
  {
    id: 'camry-8',
    make: 'Toyota',
    model: 'Camry',
    yearRange: '2018-2024',
    auction: [3000, 11000],
    type: 'Sedán',
    engine: '2.5L 4cyl',
    description:
      'El sedán mediano más vendido. Confort, espacio y confiabilidad legendaria.',
    trims: [
      {
        name: 'LE',
        auction: [3000, 6000],
        features: ['Motor 2.5L', 'Safety Sense', 'CarPlay'],
      },
      {
        name: 'SE',
        auction: [4000, 7500],
        features: ['Suspensión deportiva', 'Rines 18"', 'Modo Sport'],
      },
      {
        name: 'XLE',
        auction: [5500, 9000],
        features: ['Cuero', 'Sunroof', 'Pantalla 9"'],
      },
      {
        name: 'XSE',
        auction: [7500, 11000],
        features: ['Motor V6 (opc)', 'Rines 19"', 'JBL Audio'],
      },
    ],
  },
  {
    id: 'prius-4',
    make: 'Toyota',
    model: 'Prius',
    yearRange: '2018-2022',
    auction: [2500, 7500],
    type: 'Hatchback',
    engine: '1.8L 4cyl Híbrido',
    description:
      'El híbrido más famoso del mundo. Eficiencia extrema y confiabilidad.',
    trims: [
      {
        name: 'L Eco',
        auction: [2500, 4000],
        features: ['Híbrido', '56 MPG', 'Safety Sense'],
      },
      {
        name: 'LE',
        auction: [3500, 5000],
        features: ['Pantalla 7"', 'CarPlay', 'Climatizador auto'],
      },
      {
        name: 'XLE',
        auction: [4500, 6500],
        features: ['Cuero SofTex', 'Sunroof', 'Head-up Display'],
      },
      {
        name: 'Limited',
        auction: [5500, 7500],
        features: ['Rines 17"', 'JBL Audio', 'Carga inalámbrica'],
      },
    ],
  },
  {
    id: 'prius-5',
    make: 'Toyota',
    model: 'Prius',
    yearRange: '2023-2024',
    auction: [9000, 16000],
    type: 'Hatchback',
    engine: '2.0L 4cyl Híbrido',
    description:
      'Rediseño radical. Ahora deportivo, potente y con diseño atractivo.',
    trims: [
      {
        name: 'LE',
        auction: [9000, 11000],
        features: ['Híbrido 194hp', 'Pantalla 8"', 'Safety Sense 3.0'],
      },
      {
        name: 'XLE',
        auction: [11000, 14000],
        features: ['Pantalla 12.3"', 'Cuero SofTex', 'Sunroof'],
      },
      {
        name: 'Limited',
        auction: [13000, 16000],
        features: ['Rines 19"', 'JBL Audio', 'Head-up Display'],
      },
    ],
  },
  {
    id: 'chr-1',
    make: 'Toyota',
    model: 'C-HR',
    yearRange: '2018-2022',
    auction: [2500, 7000],
    type: 'SUV',
    engine: '2.0L 4cyl',
    description: 'Crossover compacto con diseño audaz y manejo ágil.',
    trims: [
      {
        name: 'LE',
        auction: [2500, 4000],
        features: ['Motor 2.0L', 'Safety Sense', 'Pantalla 8"'],
      },
      {
        name: 'XLE',
        auction: [3500, 5500],
        features: ['Rines 18"', 'Blind Spot', 'Climatizador dual'],
      },
      {
        name: 'Limited',
        auction: [5000, 7000],
        features: ['Cuero', 'JBL Audio', 'Carga inalámbrica'],
      },
    ],
  },
  {
    id: 'corollacross-1',
    make: 'Toyota',
    model: 'Corolla Cross',
    yearRange: '2022-2024',
    auction: [6000, 12000],
    type: 'SUV',
    engine: '2.0L 4cyl',
    description:
      'Crossover basado en Corolla. Práctico, eficiente y confiable.',
    trims: [
      {
        name: 'L',
        auction: [6000, 8000],
        features: ['Motor 2.0L', 'Safety Sense', 'CarPlay'],
      },
      {
        name: 'LE',
        auction: [7500, 10000],
        features: ['Pantalla 8"', 'Blind Spot', 'Rines 17"'],
      },
      {
        name: 'XLE',
        auction: [9500, 12000],
        features: ['Cuero SofTex', 'Sunroof', 'Power Liftgate'],
      },
    ],
  },
  {
    id: 'rav4-4',
    make: 'Toyota',
    model: 'RAV4',
    yearRange: '2018',
    auction: [4000, 6500],
    type: 'SUV',
    engine: '2.5L 4cyl',
    description: 'La última del diseño clásico. Espaciosa y muy confiable.',
    trims: [
      {
        name: 'LE',
        auction: [4000, 5000],
        features: ['Toyota Safety Sense', 'Rieles techo', 'Cámara'],
      },
      {
        name: 'XLE',
        auction: [4800, 5800],
        features: ['Sunroof', 'Climatizador', 'Rines aleación'],
      },
      {
        name: 'Limited',
        auction: [5500, 6500],
        features: ['Cuero', 'Navegación', 'Memoria asientos'],
      },
    ],
  },
  {
    id: 'rav4-5',
    make: 'Toyota',
    model: 'RAV4',
    yearRange: '2019-2024',
    auction: [7000, 16000],
    type: 'SUV',
    engine: '2.5L 4cyl',
    description: 'Estilo robusto, plataforma TNGA y excelente reventa.',
    trims: [
      {
        name: 'LE',
        auction: [7000, 10000],
        features: ['LEDs', 'CarPlay', 'Safety Sense 2.0'],
      },
      {
        name: 'XLE',
        auction: [9000, 12000],
        features: ['Rines 17"', 'Smart Key', 'Asiento eléctrico'],
      },
      {
        name: 'XLE Premium',
        auction: [11000, 14000],
        features: ['Rines 19"', 'SofTex', 'Power Liftgate'],
      },
      {
        name: 'Limited',
        auction: [13000, 16000],
        features: ['Cámaras 360', 'Asientos ventilados', 'JBL Audio'],
      },
    ],
  },
  {
    id: 'highlander-4',
    make: 'Toyota',
    model: 'Highlander',
    yearRange: '2020-2024',
    auction: [10000, 20000],
    type: 'SUV',
    engine: '3.5L V6',
    description:
      'SUV familiar de 3 filas. Confiable, espaciosa y con excelente reventa.',
    trims: [
      {
        name: 'L',
        auction: [10000, 13000],
        features: ['Motor 3.5L V6', 'Safety Sense', 'CarPlay'],
      },
      {
        name: 'LE',
        auction: [12000, 15000],
        features: ['Pantalla 8"', 'Rines 18"', 'Climatizador 3 zonas'],
      },
      {
        name: 'XLE',
        auction: [14000, 18000],
        features: ['Cuero SofTex', 'Sunroof', 'Power Liftgate'],
      },
      {
        name: 'Limited',
        auction: [17000, 20000],
        features: ['Cuero', 'JBL Audio', 'Cámaras 360'],
      },
    ],
  },
  {
    id: 'highlander-3',
    make: 'Toyota',
    model: 'Highlander',
    yearRange: '2018-2019',
    auction: [5500, 10000],
    type: 'SUV',
    engine: '3.5L V6',
    description: 'Generación anterior de 3 filas. Probada y confiable.',
    trims: [
      {
        name: 'LE',
        auction: [5500, 7000],
        features: ['Motor 3.5L V6', 'Safety Sense', 'Cámara'],
      },
      {
        name: 'XLE',
        auction: [7000, 8500],
        features: ['Sunroof', 'Cuero', 'Rines 18"'],
      },
      {
        name: 'Limited',
        auction: [8500, 10000],
        features: ['Navegación', 'JBL Audio', 'Ventilados'],
      },
    ],
  },
  {
    id: '4runner-5',
    make: 'Toyota',
    model: '4Runner',
    yearRange: '2018-2024',
    auction: [10000, 22000],
    type: 'SUV',
    engine: '4.0L V6',
    description: 'SUV body-on-frame legendaria. Capacidad off-road real.',
    trims: [
      {
        name: 'SR5',
        auction: [10000, 14000],
        features: ['Motor 4.0L V6', 'Pantalla 8"', 'Safety Sense'],
      },
      {
        name: 'SR5 Premium',
        auction: [13000, 17000],
        features: ['Cuero SofTex', 'Sunroof', 'Rines 17"'],
      },
      {
        name: 'TRD Off-Road',
        auction: [15000, 19000],
        features: ['Bloqueo diferencial', 'Crawl Control', 'KDSS'],
      },
      {
        name: 'Limited',
        auction: [18000, 22000],
        features: ['Cuero', 'JBL Audio', 'Rines 20"'],
      },
    ],
  },
  {
    id: 'tacoma-3',
    make: 'Toyota',
    model: 'Tacoma',
    yearRange: '2018-2023',
    auction: [8500, 19000],
    type: 'Pick-up',
    engine: '3.5L V6',
    description: 'Valor de reventa inigualable. Capacidad off-road legendaria.',
    trims: [
      {
        name: 'SR5',
        auction: [8500, 12000],
        features: ['Pantalla táctil', 'Safety Sense', 'Rines aleación'],
      },
      {
        name: 'TRD Sport',
        auction: [11000, 15000],
        features: ['Hood scoop', 'Rines 17"', 'Suspensión deportiva'],
      },
      {
        name: 'TRD Off-Road',
        auction: [13000, 17000],
        features: ['Bloqueo diferencial', 'Bilstein', 'Crawl Control'],
      },
      {
        name: 'Limited',
        auction: [16000, 19000],
        features: ['Cuero', 'Sunroof', 'JBL Audio'],
      },
    ],
  },
  {
    id: 'tundra-3',
    make: 'Toyota',
    model: 'Tundra',
    yearRange: '2022-2024',
    auction: [16000, 27000],
    type: 'Pick-up',
    engine: '3.5L V6 Twin Turbo',
    description:
      'Pick-up full-size totalmente nueva. Motor twin-turbo V6 potente.',
    trims: [
      {
        name: 'SR5',
        auction: [16000, 19000],
        features: ['Motor 3.5TT V6', 'Pantalla 8"', 'Safety Sense'],
      },
      {
        name: 'Limited',
        auction: [19000, 22000],
        features: ['Cuero', 'Pantalla 14"', 'JBL Audio'],
      },
      {
        name: '1794 Edition',
        auction: [22000, 25000],
        features: ['Cuero premium', 'Sunroof', 'Cámaras 360'],
      },
      {
        name: 'TRD Pro',
        auction: [24000, 27000],
        features: ['Fox shocks', 'Skid plates', 'TRD exhaust'],
      },
    ],
  },
  {
    id: 'tundra-2',
    make: 'Toyota',
    model: 'Tundra',
    yearRange: '2018-2021',
    auction: [8500, 16000],
    type: 'Pick-up',
    engine: '5.7L V8',
    description: 'Pick-up full-size con motor V8 iForce probado y confiable.',
    trims: [
      {
        name: 'SR5',
        auction: [8500, 11000],
        features: ['Motor 5.7L V8', 'Pantalla táctil', 'Safety Sense'],
      },
      {
        name: 'Limited',
        auction: [11000, 13500],
        features: ['Cuero', 'Sunroof', 'JBL Audio'],
      },
      {
        name: 'Platinum',
        auction: [13000, 16000],
        features: ['Cuero premium', 'Ventilados', 'Cámaras 360'],
      },
    ],
  },

  // ═══════════════════════════════════════
  // HONDA
  // ═══════════════════════════════════════
  {
    id: 'civic-10',
    make: 'Honda',
    model: 'Civic',
    yearRange: '2018-2021',
    auction: [2500, 7000],
    type: 'Sedán',
    engine: '2.0L 4cyl',
    description:
      'Diseño agresivo, manejo deportivo y excelente espacio interior.',
    trims: [
      {
        name: 'LX',
        auction: [2500, 3800],
        features: ['Motor 2.0L', 'Cámara reversa', 'Honda Sensing (2019+)'],
      },
      {
        name: 'Sport',
        auction: [3500, 5000],
        features: ['Escape central', 'Rines 18"', 'CarPlay'],
      },
      {
        name: 'EX',
        auction: [4200, 5800],
        features: ['Motor 1.5T', 'Sunroof', 'LaneWatch'],
      },
      {
        name: 'Touring',
        auction: [5500, 7000],
        features: ['Cuero', 'Navegación', 'LEDs Premium'],
      },
    ],
  },
  {
    id: 'civic-11',
    make: 'Honda',
    model: 'Civic',
    yearRange: '2022-2024',
    auction: [7000, 14000],
    type: 'Sedán',
    engine: '2.0L 4cyl',
    description: 'Más maduro, minimalista y con tecnología de punta.',
    trims: [
      {
        name: 'LX',
        auction: [7000, 9000],
        features: ['Pantalla 7"', 'LEDs', 'Push Button Start'],
      },
      {
        name: 'Sport',
        auction: [8500, 10500],
        features: ['Rines negros 18"', 'Modo Sport', 'Volante cuero'],
      },
      {
        name: 'EX',
        auction: [10000, 12000],
        features: ['Motor 1.5T', 'Sunroof', 'Blind Spot Info'],
      },
      {
        name: 'Touring',
        auction: [12000, 14000],
        features: ['Pantalla 9"', 'Bose Audio', 'Wireless CarPlay'],
      },
    ],
  },
  {
    id: 'accord-10',
    make: 'Honda',
    model: 'Accord',
    yearRange: '2018-2024',
    auction: [4000, 13000],
    type: 'Sedán',
    engine: '1.5L 4cyl Turbo',
    description:
      'Sedán mediano premium. Excelente manejo, espacio y tecnología.',
    trims: [
      {
        name: 'LX',
        auction: [4000, 6500],
        features: ['Motor 1.5T', 'Honda Sensing', 'CarPlay'],
      },
      {
        name: 'Sport',
        auction: [5500, 8500],
        features: ['Rines 19"', 'Escape doble', 'Volante cuero'],
      },
      {
        name: 'EX-L',
        auction: [7500, 10500],
        features: ['Cuero', 'Sunroof', 'Memoria asientos'],
      },
      {
        name: 'Touring',
        auction: [10000, 13000],
        features: ['Motor 2.0T', 'Head-up Display', 'Navegación'],
      },
    ],
  },
  {
    id: 'fit-3',
    make: 'Honda',
    model: 'Fit',
    yearRange: '2018-2020',
    auction: [1800, 4000],
    type: 'Hatchback',
    engine: '1.5L 4cyl',
    description:
      'Subcompacto mágico. Espacio interior increíble para su tamaño.',
    trims: [
      {
        name: 'LX',
        auction: [1800, 2500],
        features: ['Motor 1.5L', 'Cámara reversa', 'Bluetooth'],
      },
      {
        name: 'Sport',
        auction: [2300, 3000],
        features: ['Rines 16"', 'Paddle Shifters', 'Spoiler'],
      },
      {
        name: 'EX',
        auction: [2800, 3500],
        features: ['Sunroof', 'Honda Sensing', 'CarPlay'],
      },
      {
        name: 'EX-L',
        auction: [3200, 4000],
        features: ['Cuero', 'Navegación', 'Asientos calefactados'],
      },
    ],
  },
  {
    id: 'hrv-2',
    make: 'Honda',
    model: 'HR-V',
    yearRange: '2018-2022',
    auction: [3000, 7000],
    type: 'SUV',
    engine: '1.8L 4cyl',
    description: 'Crossover subcompacto versátil con asientos Magic Seat.',
    trims: [
      {
        name: 'LX',
        auction: [3000, 4200],
        features: ['Motor 1.8L', 'Cámara reversa', 'Bluetooth'],
      },
      {
        name: 'Sport',
        auction: [3800, 5200],
        features: ['Rines 18"', 'Pantalla 7"', 'Volante cuero'],
      },
      {
        name: 'EX',
        auction: [4500, 6000],
        features: ['Sunroof', 'Honda Sensing', 'CarPlay'],
      },
      {
        name: 'EX-L',
        auction: [5500, 7000],
        features: ['Cuero', 'Navegación', 'Asientos calefactados'],
      },
    ],
  },
  {
    id: 'hrv-3',
    make: 'Honda',
    model: 'HR-V',
    yearRange: '2023-2024',
    auction: [7000, 11500],
    type: 'SUV',
    engine: '2.0L 4cyl',
    description:
      'Totalmente rediseñada. Más grande, más premium y con motor turbo.',
    trims: [
      {
        name: 'LX',
        auction: [7000, 8500],
        features: ['Motor 2.0L', 'Pantalla 7"', 'Honda Sensing'],
      },
      {
        name: 'Sport',
        auction: [8500, 10000],
        features: ['Rines 18"', 'Pantalla 9"', 'Wireless CarPlay'],
      },
      {
        name: 'EX-L',
        auction: [9500, 11500],
        features: ['Cuero', 'Sunroof', 'Bose Audio'],
      },
    ],
  },
  {
    id: 'crv-5',
    make: 'Honda',
    model: 'CR-V',
    yearRange: '2018-2022',
    auction: [4500, 10000],
    type: 'SUV',
    engine: '1.5L 4cyl Turbo',
    description: 'La referencia en espacio y practicidad familiar.',
    trims: [
      {
        name: 'LX',
        auction: [4500, 6000],
        features: ['Motor 2.4L', 'Rines 17"', 'Climatizador'],
      },
      {
        name: 'EX',
        auction: [5500, 7500],
        features: ['Motor 1.5T', 'Sunroof', 'CarPlay'],
      },
      {
        name: 'EX-L',
        auction: [7000, 9000],
        features: ['Cuero', 'Power Tailgate', 'Memoria asientos'],
      },
      {
        name: 'Touring',
        auction: [8500, 10000],
        features: ['Navegación', 'LEDs', 'Escape doble'],
      },
    ],
  },
  {
    id: 'crv-6',
    make: 'Honda',
    model: 'CR-V',
    yearRange: '2023-2024',
    auction: [11000, 17000],
    type: 'SUV',
    engine: '1.5L 4cyl Turbo',
    description: 'Más grande, más premium y con diseño robusto.',
    trims: [
      {
        name: 'EX',
        auction: [11000, 13000],
        features: ['Motor 1.5T', 'Honda Sensing', 'Rines 18"'],
      },
      {
        name: 'EX-L',
        auction: [13000, 15000],
        features: ['Pantalla 9"', 'Wireless CarPlay', 'Cuero'],
      },
      {
        name: 'Sport Touring',
        auction: [15000, 17000],
        features: ['Híbrido', 'Rines negros 19"', 'Bose'],
      },
    ],
  },
  {
    id: 'pilot-4',
    make: 'Honda',
    model: 'Pilot',
    yearRange: '2019-2024',
    auction: [8000, 18000],
    type: 'SUV',
    engine: '3.5L V6',
    description: 'SUV de 3 filas familiar. Espaciosa, cómoda y bien equipada.',
    trims: [
      {
        name: 'LX',
        auction: [8000, 11000],
        features: ['Motor 3.5L V6', 'Honda Sensing', 'CarPlay'],
      },
      {
        name: 'EX-L',
        auction: [10500, 14000],
        features: ['Cuero', 'Sunroof', 'Power Tailgate'],
      },
      {
        name: 'Touring',
        auction: [13000, 16000],
        features: ['Navegación', 'Rear Entertainment', 'Ventilados'],
      },
      {
        name: 'Elite/Black Edition',
        auction: [15500, 18000],
        features: ['Rines 20"', 'Wireless CarPlay', 'Cámaras 360'],
      },
    ],
  },
  {
    id: 'passport-1',
    make: 'Honda',
    model: 'Passport',
    yearRange: '2019-2024',
    auction: [7000, 15000],
    type: 'SUV',
    engine: '3.5L V6',
    description:
      'SUV mediana aventurera. Más robusta que CR-V, más ágil que Pilot.',
    trims: [
      {
        name: 'Sport',
        auction: [7000, 10000],
        features: ['Motor 3.5L V6', 'Honda Sensing', 'Rines 20"'],
      },
      {
        name: 'EX-L',
        auction: [9500, 12500],
        features: ['Cuero', 'Sunroof', 'Power Tailgate'],
      },
      {
        name: 'Touring',
        auction: [12000, 15000],
        features: ['Navegación', 'Ventilados', 'Wireless CarPlay'],
      },
    ],
  },
  {
    id: 'ridgeline-2',
    make: 'Honda',
    model: 'Ridgeline',
    yearRange: '2018-2024',
    auction: [7000, 16000],
    type: 'Pick-up',
    engine: '3.5L V6',
    description:
      'Pick-up unibody única. Manejo de auto, utilidad de camioneta.',
    trims: [
      {
        name: 'Sport',
        auction: [7000, 10000],
        features: ['Motor 3.5L V6', 'Pantalla 8"', 'CarPlay'],
      },
      {
        name: 'RTL',
        auction: [9500, 12500],
        features: ['Cuero', 'Sunroof', 'Power Tailgate'],
      },
      {
        name: 'RTL-E',
        auction: [11500, 14000],
        features: ['Navegación', 'Bed Audio', 'Ventilados'],
      },
      {
        name: 'Black Edition',
        auction: [13000, 16000],
        features: ['Rines negros', 'Interior rojo/negro', 'Wireless CarPlay'],
      },
    ],
  },

  // ═══════════════════════════════════════
  // HYUNDAI
  // ═══════════════════════════════════════
  {
    id: 'accent-5',
    make: 'Hyundai',
    model: 'Accent',
    yearRange: '2018-2022',
    auction: [800, 2500],
    type: 'Sedán',
    engine: '1.6L 4cyl',
    description:
      'Subcompacto económico. Ideal para primer auto o presupuesto ajustado.',
    trims: [
      {
        name: 'SE',
        auction: [800, 1500],
        features: ['Motor 1.6L', 'Pantalla 5"', 'Bluetooth'],
      },
      {
        name: 'SEL',
        auction: [1200, 2000],
        features: ['Pantalla 7"', 'CarPlay', 'Cámara reversa'],
      },
      {
        name: 'Limited',
        auction: [1800, 2500],
        features: ['Sunroof', 'Cuero parcial', 'Push Start'],
      },
    ],
  },
  {
    id: 'elantra-6',
    make: 'Hyundai',
    model: 'Elantra',
    yearRange: '2018-2020',
    auction: [1200, 3200],
    type: 'Sedán',
    engine: '2.0L 4cyl',
    description: 'Diseño angular y excelente equipamiento por el precio.',
    trims: [
      {
        name: 'SE',
        auction: [1200, 2000],
        features: ['Cámara reversa', 'Bluetooth', 'Modos de manejo'],
      },
      {
        name: 'SEL',
        auction: [1800, 2600],
        features: ['Pantalla 7"', 'CarPlay', 'Blind Spot'],
      },
      {
        name: 'Limited',
        auction: [2400, 3200],
        features: ['Cuero', 'LEDs', 'Carga inalámbrica'],
      },
    ],
  },
  {
    id: 'elantra-7',
    make: 'Hyundai',
    model: 'Elantra',
    yearRange: '2021-2024',
    auction: [3500, 8000],
    type: 'Sedán',
    engine: '2.0L 4cyl',
    description:
      'Diseño futurista "Parametric Dynamics" y tecnología avanzada.',
    trims: [
      {
        name: 'SE',
        auction: [3500, 5000],
        features: ['Pantalla 8"', 'Wireless CarPlay', 'Frenado auto'],
      },
      {
        name: 'SEL',
        auction: [4500, 6500],
        features: ['Llave inteligente', 'Climatizador dual', 'BlueLink'],
      },
      {
        name: 'Limited',
        auction: [6000, 8000],
        features: ['Pantallas duales 10.25"', 'Bose', 'Cuero'],
      },
    ],
  },
  {
    id: 'sonata-7',
    make: 'Hyundai',
    model: 'Sonata',
    yearRange: '2018-2019',
    auction: [2000, 4500],
    type: 'Sedán',
    engine: '2.4L 4cyl',
    description: 'Sedán mediano espacioso con buen equipamiento.',
    trims: [
      {
        name: 'SE',
        auction: [2000, 2800],
        features: ['Motor 2.4L', 'Pantalla 7"', 'CarPlay'],
      },
      {
        name: 'SEL',
        auction: [2600, 3600],
        features: ['Blind Spot', 'Asientos calefactados', 'Push Start'],
      },
      {
        name: 'Limited',
        auction: [3400, 4500],
        features: ['Cuero', 'Sunroof', 'Infinity Audio'],
      },
    ],
  },
  {
    id: 'sonata-8',
    make: 'Hyundai',
    model: 'Sonata',
    yearRange: '2020-2024',
    auction: [4000, 10000],
    type: 'Sedán',
    engine: '2.5L 4cyl',
    description: 'Diseño dramático y tecnología de clase superior.',
    trims: [
      {
        name: 'SE',
        auction: [4000, 6000],
        features: ['Motor 2.5L', 'Pantalla 8"', 'Safety Tech'],
      },
      {
        name: 'SEL',
        auction: [5500, 8000],
        features: ['Sunroof', 'Asientos calefactados', 'BlueLink'],
      },
      {
        name: 'Limited',
        auction: [7500, 10000],
        features: ['Cuero Nappa', 'Bose', 'Head-up Display'],
      },
    ],
  },
  {
    id: 'venue-1',
    make: 'Hyundai',
    model: 'Venue',
    yearRange: '2020-2024',
    auction: [2000, 4500],
    type: 'SUV',
    engine: '1.6L 4cyl',
    description: 'SUV subcompacta urbana. Económica y fácil de manejar.',
    trims: [
      {
        name: 'SE',
        auction: [2000, 2800],
        features: ['Motor 1.6L', 'Pantalla 8"', 'CarPlay'],
      },
      {
        name: 'SEL',
        auction: [2800, 3600],
        features: ['Blind Spot', 'Asientos calefactados', 'Push Start'],
      },
      {
        name: 'Limited',
        auction: [3500, 4500],
        features: ['Cuero parcial', 'Sunroof', 'Bose'],
      },
    ],
  },
  {
    id: 'kona-2',
    make: 'Hyundai',
    model: 'Kona',
    yearRange: '2018-2021',
    auction: [3000, 7000],
    type: 'SUV',
    engine: '2.0L 4cyl',
    description:
      'Crossover subcompacto con personalidad. Ágil y bien equipado.',
    trims: [
      {
        name: 'SE',
        auction: [3000, 4200],
        features: ['Motor 2.0L', 'Pantalla 7"', 'Safety Tech'],
      },
      {
        name: 'SEL',
        auction: [3800, 5200],
        features: ['Blind Spot', 'Asientos calefactados', 'Rines 17"'],
      },
      {
        name: 'Limited',
        auction: [5200, 7000],
        features: ['Cuero', 'Sunroof', 'Head-up Display'],
      },
    ],
  },
  {
    id: 'kona-3',
    make: 'Hyundai',
    model: 'Kona',
    yearRange: '2022-2024',
    auction: [5000, 8500],
    type: 'SUV',
    engine: '2.0L 4cyl',
    description: 'Kona actualizada con N Line deportivo y más tecnología.',
    trims: [
      {
        name: 'SE',
        auction: [5000, 6000],
        features: ['Motor 2.0L', 'Pantalla 8"', 'Safety Tech'],
      },
      {
        name: 'SEL',
        auction: [5800, 6800],
        features: ['Blind Spot', 'Asientos calefactados', 'Rines 17"'],
      },
      {
        name: 'N Line',
        auction: [6500, 7500],
        features: ['Motor 1.6T', 'Rines 18"', 'Interior deportivo'],
      },
      {
        name: 'Limited',
        auction: [7200, 8500],
        features: ['Cuero', 'Sunroof', 'Head-up Display'],
      },
    ],
  },
  {
    id: 'tucson-3',
    make: 'Hyundai',
    model: 'Tucson',
    yearRange: '2018-2021',
    auction: [3000, 7500],
    type: 'SUV',
    engine: '2.0L 4cyl',
    description: 'SUV compacta confiable con buen espacio y equipamiento.',
    trims: [
      {
        name: 'SE',
        auction: [3000, 4200],
        features: ['Motor 2.0L', 'Pantalla 7"', 'Blind Spot'],
      },
      {
        name: 'SEL',
        auction: [4000, 5500],
        features: ['Rines 17"', 'Asientos calefactados', 'CarPlay'],
      },
      {
        name: 'Limited',
        auction: [5500, 7500],
        features: ['Cuero', 'Techo panorámico', 'Infinity Audio'],
      },
    ],
  },
  {
    id: 'tucson-4',
    make: 'Hyundai',
    model: 'Tucson',
    yearRange: '2022-2024',
    auction: [7000, 13000],
    type: 'SUV',
    engine: '2.5L 4cyl',
    description: 'Diseño futurista, interior premium y opción híbrida.',
    trims: [
      {
        name: 'SE',
        auction: [7000, 9000],
        features: ['Pantalla 8"', 'Safety Tech', 'Rines 17"'],
      },
      {
        name: 'SEL',
        auction: [8500, 11000],
        features: ['Pantalla 10.25"', 'Llave digital', 'Blind Spot'],
      },
      {
        name: 'Limited',
        auction: [10500, 13000],
        features: ['Cuero', 'Techo panorámico', 'Bose'],
      },
    ],
  },
  {
    id: 'santafe-5',
    make: 'Hyundai',
    model: 'Santa Fe',
    yearRange: '2019-2024',
    auction: [5000, 13000],
    type: 'SUV',
    engine: '2.5L 4cyl',
    description: 'SUV familiar con 3 filas opcional. Potente y bien equipada.',
    trims: [
      {
        name: 'SE',
        auction: [5000, 7500],
        features: ['Motor 2.5L', 'Safety Tech', 'CarPlay'],
      },
      {
        name: 'SEL',
        auction: [7000, 10000],
        features: ['Rines 18"', 'Asientos calefactados', 'BlueLink'],
      },
      {
        name: 'Limited',
        auction: [10000, 13000],
        features: ['Cuero', 'Techo panorámico', 'Harman Kardon'],
      },
    ],
  },
  {
    id: 'palisade-1',
    make: 'Hyundai',
    model: 'Palisade',
    yearRange: '2020-2024',
    auction: [11000, 21000],
    type: 'SUV',
    engine: '3.8L V6',
    description:
      'SUV grande premium de 3 filas. Compite con lujo a precio accesible.',
    trims: [
      {
        name: 'SE',
        auction: [11000, 14000],
        features: ['Motor 3.8L V6', 'Pantalla 8"', 'Safety Tech'],
      },
      {
        name: 'SEL',
        auction: [13500, 16500],
        features: ['Cuero', 'Sunroof', 'Asientos calefactados 2 filas'],
      },
      {
        name: 'Limited',
        auction: [16000, 19000],
        features: ['Cuero Nappa', 'Harman Kardon', 'Head-up Display'],
      },
      {
        name: 'Calligraphy',
        auction: [18500, 21000],
        features: ['Quilted Nappa', 'Rines 20"', 'Intercom'],
      },
    ],
  },

  // ═══════════════════════════════════════
  // KIA
  // ═══════════════════════════════════════
  {
    id: 'rio-4',
    make: 'Kia',
    model: 'Rio',
    yearRange: '2018-2023',
    auction: [1000, 2800],
    type: 'Sedán',
    engine: '1.6L 4cyl',
    description: 'Subcompacto económico con buena garantía y equipamiento.',
    trims: [
      {
        name: 'LX',
        auction: [1000, 1600],
        features: ['Motor 1.6L', 'Pantalla 5"', 'Bluetooth'],
      },
      {
        name: 'S',
        auction: [1400, 2200],
        features: ['Pantalla 7"', 'CarPlay', 'Cámara reversa'],
      },
      {
        name: 'EX',
        auction: [2000, 2800],
        features: ['Sunroof', 'Push Start', 'Rines 16"'],
      },
    ],
  },
  {
    id: 'forte-3',
    make: 'Kia',
    model: 'Forte',
    yearRange: '2019-2024',
    auction: [2200, 6000],
    type: 'Sedán',
    engine: '2.0L 4cyl',
    description: 'Sedán compacto con excelente equipamiento y garantía líder.',
    trims: [
      {
        name: 'FE',
        auction: [2200, 3500],
        features: ['Motor 2.0L', 'Pantalla 8"', 'CarPlay'],
      },
      {
        name: 'LXS',
        auction: [3000, 4500],
        features: ['Rines 16"', 'Asiento conductor eléctrico', 'Blind Spot'],
      },
      {
        name: 'GT-Line',
        auction: [4000, 5500],
        features: ['Rines 18"', 'Interior deportivo', 'LEDs'],
      },
      {
        name: 'GT',
        auction: [4800, 6000],
        features: ['Motor 1.6T', 'Suspensión independiente', 'Cuero'],
      },
    ],
  },
  {
    id: 'optima-4',
    make: 'Kia',
    model: 'Optima',
    yearRange: '2018-2020',
    auction: [2000, 5000],
    type: 'Sedán',
    engine: '2.4L 4cyl',
    description: 'Sedán mediano elegante. Predecesor del K5.',
    trims: [
      {
        name: 'LX',
        auction: [2000, 3000],
        features: ['Motor 2.4L', 'Pantalla 7"', 'CarPlay'],
      },
      {
        name: 'S',
        auction: [2800, 3800],
        features: ['Rines 18"', 'Interior deportivo', 'Push Start'],
      },
      {
        name: 'EX',
        auction: [3500, 4500],
        features: ['Cuero', 'Sunroof', 'Asientos calefactados'],
      },
      {
        name: 'SX',
        auction: [4000, 5000],
        features: ['Motor 2.0T', 'Rines 18"', 'Harman Kardon'],
      },
    ],
  },
  {
    id: 'k5-1',
    make: 'Kia',
    model: 'K5',
    yearRange: '2021-2024',
    auction: [5000, 11500],
    type: 'Sedán',
    engine: '1.6L 4cyl Turbo',
    description: 'Reemplazo del Optima. Diseño audaz y manejo deportivo.',
    trims: [
      {
        name: 'LXS',
        auction: [5000, 7000],
        features: ['Motor 1.6T', 'Pantalla 8"', 'Safety Tech'],
      },
      {
        name: 'GT-Line',
        auction: [6500, 8500],
        features: ['Rines 18"', 'Interior deportivo', 'Techo panorámico'],
      },
      {
        name: 'EX',
        auction: [8000, 10000],
        features: ['Cuero', 'Asientos ventilados', 'Bose'],
      },
      {
        name: 'GT',
        auction: [9500, 11500],
        features: ['Motor 2.5T', 'Rines 19"', 'Launch Control'],
      },
    ],
  },
  {
    id: 'niro-2',
    make: 'Kia',
    model: 'Niro',
    yearRange: '2023-2024',
    auction: [7000, 11500],
    type: 'SUV',
    engine: '1.6L 4cyl Híbrido',
    description: 'Crossover híbrido eficiente con diseño moderno.',
    trims: [
      {
        name: 'LX',
        auction: [7000, 8500],
        features: ['Híbrido', 'Pantalla 8"', 'Safety Tech'],
      },
      {
        name: 'EX',
        auction: [8500, 10000],
        features: ['Pantalla 10.25"', 'Cuero sintético', 'Sunroof'],
      },
      {
        name: 'SX Touring',
        auction: [10000, 11500],
        features: ['Harman Kardon', 'Head-up Display', 'Ventilados'],
      },
    ],
  },
  {
    id: 'seltos-1',
    make: 'Kia',
    model: 'Seltos',
    yearRange: '2021-2024',
    auction: [4500, 9000],
    type: 'SUV',
    engine: '2.0L 4cyl',
    description: 'SUV subcompacta con mucho estilo y tecnología por el precio.',
    trims: [
      {
        name: 'LX',
        auction: [4500, 5800],
        features: ['Motor 2.0L', 'Pantalla 8"', 'Safety Tech'],
      },
      {
        name: 'S',
        auction: [5500, 7000],
        features: ['Rines 17"', 'Sunroof', 'LED DRL'],
      },
      {
        name: 'EX',
        auction: [6500, 8000],
        features: ['Cuero sintético', 'Blind Spot', 'Carga inalámbrica'],
      },
      {
        name: 'SX Turbo',
        auction: [7500, 9000],
        features: ['Motor 1.6T', 'Rines 18"', 'Bose'],
      },
    ],
  },
  {
    id: 'sportage-4',
    make: 'Kia',
    model: 'Sportage',
    yearRange: '2018-2022',
    auction: [3000, 8500],
    type: 'SUV',
    engine: '2.4L 4cyl',
    description: 'SUV compacta probada con buen espacio y equipamiento.',
    trims: [
      {
        name: 'LX',
        auction: [3000, 4500],
        features: ['Motor 2.4L', 'Pantalla 7"', 'Cámara reversa'],
      },
      {
        name: 'S',
        auction: [4000, 5800],
        features: ['Rines 18"', 'CarPlay', 'Blind Spot'],
      },
      {
        name: 'EX',
        auction: [5200, 7000],
        features: ['Cuero', 'Sunroof', 'Asientos calefactados'],
      },
      {
        name: 'SX Turbo',
        auction: [6500, 8500],
        features: ['Motor 2.0T', 'Rines 19"', 'Harman Kardon'],
      },
    ],
  },
  {
    id: 'sportage-5',
    make: 'Kia',
    model: 'Sportage',
    yearRange: '2023-2024',
    auction: [8500, 16000],
    type: 'SUV',
    engine: '2.5L 4cyl',
    description:
      'Diseño revolucionario con pantalla curva dual y mucho espacio.',
    trims: [
      {
        name: 'LX',
        auction: [8500, 10500],
        features: ['Pantalla dual 12.3"', 'Safety Tech', 'Rines 17"'],
      },
      {
        name: 'EX',
        auction: [10000, 12500],
        features: ['Cuero sintético', 'Sunroof', 'Asientos calefactados'],
      },
      {
        name: 'SX',
        auction: [12000, 14500],
        features: ['Motor 2.5T', 'Rines 19"', 'Harman Kardon'],
      },
      {
        name: 'SX Prestige',
        auction: [14000, 16000],
        features: ['Cuero Nappa', 'Asientos ventilados', 'Head-up Display'],
      },
    ],
  },
  {
    id: 'sorento-4',
    make: 'Kia',
    model: 'Sorento',
    yearRange: '2021-2024',
    auction: [7000, 15000],
    type: 'SUV',
    engine: '2.5L 4cyl',
    description: 'SUV mediana de 3 filas con opciones turbo e híbrida.',
    trims: [
      {
        name: 'LX',
        auction: [7000, 9500],
        features: ['Motor 2.5L', 'Pantalla 8"', 'Safety Tech'],
      },
      {
        name: 'S',
        auction: [9000, 11000],
        features: ['Rines 18"', 'Cuero sintético', 'Sunroof'],
      },
      {
        name: 'EX',
        auction: [10500, 13000],
        features: ['Motor 2.5T', 'Cuero', 'Bose'],
      },
      {
        name: 'SX',
        auction: [12500, 15000],
        features: ['Rines 20"', 'Ventilados', 'Head-up Display'],
      },
    ],
  },
  {
    id: 'telluride-1',
    make: 'Kia',
    model: 'Telluride',
    yearRange: '2020-2024',
    auction: [11000, 21000],
    type: 'SUV',
    engine: '3.8L V6',
    description:
      'SUV grande premium de 3 filas. Ganadora de múltiples premios.',
    trims: [
      {
        name: 'LX',
        auction: [11000, 14000],
        features: ['Motor 3.8L V6', 'Pantalla 8"', 'Safety Tech'],
      },
      {
        name: 'S',
        auction: [13500, 16500],
        features: ['Rines 20"', 'Cuero sintético', 'Sunroof'],
      },
      {
        name: 'EX',
        auction: [16000, 19000],
        features: ['Cuero', 'Harman Kardon', 'Asientos calefactados 2 filas'],
      },
      {
        name: 'SX',
        auction: [18500, 21000],
        features: ['Cuero Nappa', 'Head-up Display', 'Cámaras 360'],
      },
    ],
  },
  {
    id: 'soul-3',
    make: 'Kia',
    model: 'Soul',
    yearRange: '2020-2024',
    auction: [2500, 6000],
    type: 'Hatchback',
    engine: '2.0L 4cyl',
    description:
      'Diseño único y personalidad propia. Muy espacioso por dentro.',
    trims: [
      {
        name: 'LX',
        auction: [2500, 3500],
        features: ['Motor 2.0L', 'Pantalla 7"', 'Safety Tech'],
      },
      {
        name: 'S',
        auction: [3200, 4200],
        features: ['Rines 16"', 'CarPlay', 'Cámara reversa'],
      },
      {
        name: 'EX',
        auction: [4000, 5200],
        features: ['Sunroof', 'Cuero sintético', 'Harman Kardon'],
      },
      {
        name: 'Turbo',
        auction: [4800, 6000],
        features: ['Motor 1.6T', 'Rines 18"', 'Head-up Display'],
      },
    ],
  },

  // ═══════════════════════════════════════
  // NISSAN
  // ═══════════════════════════════════════
  {
    id: 'versa-3',
    make: 'Nissan',
    model: 'Versa',
    yearRange: '2018-2019',
    auction: [400, 1200],
    type: 'Sedán',
    engine: '1.6L 4cyl',
    description: 'El sedán más económico del mercado. Básico pero funcional.',
    trims: [
      {
        name: 'S',
        auction: [400, 700],
        features: ['Motor 1.6L', 'Bluetooth', 'Básico'],
      },
      {
        name: 'SV',
        auction: [600, 1000],
        features: ['Pantalla 7"', 'Cámara reversa', 'Crucero'],
      },
      {
        name: 'SR',
        auction: [800, 1200],
        features: ['Rines 16"', 'Spoiler', 'Interior deportivo'],
      },
    ],
  },
  {
    id: 'versa-4',
    make: 'Nissan',
    model: 'Versa',
    yearRange: '2020-2024',
    auction: [1800, 4500],
    type: 'Sedán',
    engine: '1.6L 4cyl',
    description: 'Totalmente rediseñado. Mucho mejor calidad y seguridad.',
    trims: [
      {
        name: 'S',
        auction: [1800, 2600],
        features: ['Motor 1.6L', 'Safety Shield 360', 'CarPlay'],
      },
      {
        name: 'SV',
        auction: [2400, 3500],
        features: ['Pantalla 7"', 'Push Start', 'Blind Spot'],
      },
      {
        name: 'SR',
        auction: [3200, 4500],
        features: ['Rines 17"', 'LEDs', 'Interior deportivo'],
      },
    ],
  },
  {
    id: 'sentra-b17',
    make: 'Nissan',
    model: 'Sentra',
    yearRange: '2018-2019',
    auction: [1000, 2200],
    type: 'Sedán',
    engine: '1.8L 4cyl',
    description:
      'Opción económica y espaciosa. Ideal para presupuesto ajustado.',
    trims: [
      {
        name: 'S',
        auction: [1000, 1400],
        features: ['Básico', 'Económico', 'Bluetooth'],
      },
      {
        name: 'SV',
        auction: [1300, 1800],
        features: ['Pantalla 5"', 'Llave inteligente', 'Rines 16"'],
      },
      {
        name: 'SR',
        auction: [1700, 2200],
        features: ['Deportivo', 'Rines 17"', 'Spoiler'],
      },
    ],
  },
  {
    id: 'sentra-b18',
    make: 'Nissan',
    model: 'Sentra',
    yearRange: '2020-2024',
    auction: [2800, 6500],
    type: 'Sedán',
    engine: '2.0L 4cyl',
    description:
      'Salto gigante en calidad. Diseño atractivo y mucha seguridad.',
    trims: [
      {
        name: 'S',
        auction: [2800, 4000],
        features: ['Safety Shield 360', 'Pantalla 7"', 'Push Start'],
      },
      {
        name: 'SV',
        auction: [3800, 5200],
        features: ['Pantalla 8"', 'CarPlay', 'Control Crucero Adaptativo'],
      },
      {
        name: 'SR',
        auction: [5000, 6500],
        features: ['Rines 18"', 'Luces LED', 'Interior deportivo'],
      },
    ],
  },
  {
    id: 'altima-6',
    make: 'Nissan',
    model: 'Altima',
    yearRange: '2019-2024',
    auction: [3500, 9000],
    type: 'Sedán',
    engine: '2.5L 4cyl',
    description: 'Sedán mediano con AWD disponible y motor turbo eficiente.',
    trims: [
      {
        name: 'S',
        auction: [3500, 5000],
        features: ['Motor 2.5L', 'Safety Shield 360', 'Pantalla 8"'],
      },
      {
        name: 'SV',
        auction: [4500, 6500],
        features: ['ProPILOT Assist', 'Rines 17"', 'Asientos calefactados'],
      },
      {
        name: 'SL',
        auction: [6000, 7800],
        features: ['Cuero', 'Bose', 'Sunroof'],
      },
      {
        name: 'Platinum',
        auction: [7500, 9000],
        features: ['Rines 19"', 'Cuero semi-anilina', 'Head-up Display'],
      },
    ],
  },
  {
    id: 'maxima-8',
    make: 'Nissan',
    model: 'Maxima',
    yearRange: '2018-2023',
    auction: [4000, 10000],
    type: 'Sedán',
    engine: '3.5L V6',
    description: 'Sedán deportivo de lujo. Motor V6 potente y diseño agresivo.',
    trims: [
      {
        name: 'SV',
        auction: [4000, 6000],
        features: ['Motor 3.5L V6', 'Pantalla 8"', 'Safety Shield'],
      },
      {
        name: 'SL',
        auction: [5500, 8000],
        features: ['Cuero', 'Bose', 'Sunroof'],
      },
      {
        name: 'Platinum',
        auction: [7500, 10000],
        features: ['Cuero semi-anilina', 'Rines 19"', 'Head-up Display'],
      },
    ],
  },
  {
    id: 'kicks-1',
    make: 'Nissan',
    model: 'Kicks',
    yearRange: '2018-2024',
    auction: [2200, 5500],
    type: 'SUV',
    engine: '1.6L 4cyl',
    description: 'Crossover subcompacto urbano. Económico y bien equipado.',
    trims: [
      {
        name: 'S',
        auction: [2200, 3200],
        features: ['Motor 1.6L', 'Pantalla 7"', 'CarPlay'],
      },
      {
        name: 'SV',
        auction: [3000, 4200],
        features: ['Pantalla 8"', 'Blind Spot', 'Push Start'],
      },
      {
        name: 'SR',
        auction: [4000, 5500],
        features: ['Rines 17"', 'Bose', 'Techo flotante'],
      },
    ],
  },
  {
    id: 'rogue-2',
    make: 'Nissan',
    model: 'Rogue',
    yearRange: '2018-2020',
    auction: [2500, 4500],
    type: 'SUV',
    engine: '2.5L 4cyl',
    description: 'Cómoda, asientos Zero Gravity y buen espacio de carga.',
    trims: [
      {
        name: 'S',
        auction: [2500, 3200],
        features: ['CarPlay', 'Blind Spot', 'Frenado auto'],
      },
      {
        name: 'SV',
        auction: [3000, 3800],
        features: ['Rines 17"', 'Asientos calefactados', 'Motion Liftgate'],
      },
      {
        name: 'SL',
        auction: [3600, 4500],
        features: ['Cuero', 'Navegación', 'Cámaras 360'],
      },
    ],
  },
  {
    id: 'rogue-3',
    make: 'Nissan',
    model: 'Rogue',
    yearRange: '2021-2024',
    auction: [5500, 12000],
    type: 'SUV',
    engine: '1.5L 3cyl Turbo',
    description:
      'Interior de nivel premium, motor turbo (2022+) y mucha tecnología.',
    trims: [
      {
        name: 'S',
        auction: [5500, 7500],
        features: ['LEDs', 'Rines 17"', 'Safety Shield 360'],
      },
      {
        name: 'SV',
        auction: [7000, 9000],
        features: ['ProPILOT Assist', 'Rines 18"', 'WiFi'],
      },
      {
        name: 'SL',
        auction: [8500, 10500],
        features: ['Cuero', 'Techo panorámico', 'Rines 19"'],
      },
      {
        name: 'Platinum',
        auction: [10000, 12000],
        features: ['Tablero digital', 'Cuero Nappa', 'Head-up Display'],
      },
    ],
  },
  {
    id: 'murano-3',
    make: 'Nissan',
    model: 'Murano',
    yearRange: '2018-2024',
    auction: [4500, 11500],
    type: 'SUV',
    engine: '3.5L V6',
    description: 'SUV mediana premium con diseño elegante y confort superior.',
    trims: [
      {
        name: 'S',
        auction: [4500, 6500],
        features: ['Motor 3.5L V6', 'Pantalla 8"', 'Safety Shield'],
      },
      {
        name: 'SV',
        auction: [6000, 8500],
        features: ['Rines 18"', 'Asientos calefactados', 'Motion Liftgate'],
      },
      {
        name: 'SL',
        auction: [8000, 10000],
        features: ['Cuero', 'Bose', 'Sunroof'],
      },
      {
        name: 'Platinum',
        auction: [9500, 11500],
        features: ['Rines 20"', 'Cuero semi-anilina', 'Cámaras 360'],
      },
    ],
  },
  {
    id: 'pathfinder-5',
    make: 'Nissan',
    model: 'Pathfinder',
    yearRange: '2022-2024',
    auction: [8000, 16000],
    type: 'SUV',
    engine: '3.5L V6',
    description: 'SUV de 3 filas rediseñada. Más robusta y con 9 velocidades.',
    trims: [
      {
        name: 'S',
        auction: [8000, 10500],
        features: ['Motor 3.5L V6', 'Pantalla 8"', 'Safety Shield 360'],
      },
      {
        name: 'SV',
        auction: [10000, 12500],
        features: ['Rines 18"', 'Asientos calefactados', 'Motion Liftgate'],
      },
      {
        name: 'SL',
        auction: [12000, 14500],
        features: ['Cuero', 'Bose', 'Techo panorámico'],
      },
      {
        name: 'Platinum',
        auction: [14000, 16000],
        features: ['Rines 20"', 'Cuero semi-anilina', 'Head-up Display'],
      },
    ],
  },
  {
    id: 'frontier-3',
    make: 'Nissan',
    model: 'Frontier',
    yearRange: '2022-2024',
    auction: [7500, 15000],
    type: 'Pick-up',
    engine: '3.8L V6',
    description: 'Pick-up mediana totalmente rediseñada. Motor V6 potente.',
    trims: [
      {
        name: 'S',
        auction: [7500, 10000],
        features: ['Motor 3.8L V6', 'Pantalla 8"', 'Safety Shield 360'],
      },
      {
        name: 'SV',
        auction: [9500, 12000],
        features: ['Rines 17"', 'Asientos calefactados', 'Blind Spot'],
      },
      {
        name: 'PRO-4X',
        auction: [11500, 13500],
        features: ['Bilstein', 'Bloqueo diferencial', 'Skid plates'],
      },
      {
        name: 'PRO-X',
        auction: [13000, 15000],
        features: ['Rines 17" off-road', 'Fender Audio', 'Cuero'],
      },
    ],
  },

  // ═══════════════════════════════════════
  // MAZDA
  // ═══════════════════════════════════════
  {
    id: 'mazda3-sedan',
    make: 'Mazda',
    model: '3',
    yearRange: '2019-2024',
    auction: [3500, 8500],
    type: 'Sedán',
    engine: '2.5L 4cyl',
    description: 'Diseño Kodo premiado. Interior casi de lujo en un compacto.',
    trims: [
      {
        name: 'Base',
        auction: [3500, 5000],
        features: ['Motor 2.5L', 'LEDs', 'Pantalla 8.8"'],
      },
      {
        name: 'Preferred',
        auction: [4500, 6500],
        features: ['Asiento eléctrico', 'Memoria', 'Blind Spot'],
      },
      {
        name: 'Premium',
        auction: [6000, 8500],
        features: ['Cuero', 'Head-up Display', 'Bose'],
      },
    ],
  },
  {
    id: 'mazda3-hatch',
    make: 'Mazda',
    model: '3 Hatchback',
    yearRange: '2019-2024',
    auction: [4000, 9500],
    type: 'Hatchback',
    engine: '2.5L 4cyl',
    description:
      'Diseño Kodo premiado en formato hatchback. Manejo excepcional.',
    trims: [
      {
        name: 'Base',
        auction: [4000, 5500],
        features: ['Motor 2.5L', 'LEDs', 'Pantalla 8.8"'],
      },
      {
        name: 'Preferred',
        auction: [5000, 7000],
        features: ['Asiento eléctrico', 'Memoria', 'Sunroof'],
      },
      {
        name: 'Premium',
        auction: [6500, 9500],
        features: ['Cuero', 'Head-up Display', 'Luces adaptativas'],
      },
    ],
  },
  {
    id: 'mazda6-3',
    make: 'Mazda',
    model: '6',
    yearRange: '2018-2021',
    auction: [3000, 7500],
    type: 'Sedán',
    engine: '2.5L 4cyl',
    description: 'Sedán mediano premium. Manejo europeo a precio japonés.',
    trims: [
      {
        name: 'Sport',
        auction: [3000, 4200],
        features: ['Motor 2.5L', 'Pantalla 8"', 'LEDs'],
      },
      {
        name: 'Touring',
        auction: [4000, 5500],
        features: ['Cuero', 'Sunroof', 'Bose'],
      },
      {
        name: 'Grand Touring',
        auction: [5000, 6500],
        features: ['Motor 2.5T', 'Rines 19"', 'Nappa'],
      },
      {
        name: 'Signature',
        auction: [6000, 7500],
        features: ['Cuero Nappa', 'Madera real', 'Ventilados'],
      },
    ],
  },
  {
    id: 'cx30',
    make: 'Mazda',
    model: 'CX-30',
    yearRange: '2020-2024',
    auction: [4000, 9000],
    type: 'SUV',
    engine: '2.5L 4cyl',
    description: 'Crossover compacto con interior premium y manejo ágil.',
    trims: [
      {
        name: 'Base',
        auction: [4000, 5500],
        features: ['Motor 2.5L', 'Pantalla 8.8"', 'Blind Spot'],
      },
      {
        name: 'Preferred',
        auction: [5000, 6800],
        features: ['Asiento eléctrico', 'Rines 18"', 'Sunroof'],
      },
      {
        name: 'Premium',
        auction: [6200, 8000],
        features: ['Cuero', 'Bose', 'Head-up Display'],
      },
      {
        name: 'Turbo',
        auction: [7500, 9000],
        features: ['Motor 2.5T', 'Rines 18"', 'Nappa'],
      },
    ],
  },
  {
    id: 'cx5',
    make: 'Mazda',
    model: 'CX-5',
    yearRange: '2018-2024',
    auction: [4000, 10000],
    type: 'SUV',
    engine: '2.5L 4cyl',
    description: 'La SUV para quienes aman manejar. Acabados de lujo.',
    trims: [
      {
        name: 'Sport',
        auction: [4000, 5500],
        features: ['LEDs', 'Blind Spot', 'Rines 17"'],
      },
      {
        name: 'Touring',
        auction: [5200, 7000],
        features: ['Cuero sintético', 'CarPlay', 'Keyless Entry'],
      },
      {
        name: 'Grand Touring',
        auction: [6500, 8500],
        features: ['Cuero', 'Sunroof', 'Bose', 'Rines 19"'],
      },
      {
        name: 'Signature',
        auction: [8000, 10000],
        features: ['Motor Turbo', 'Nappa', 'Cámaras 360'],
      },
    ],
  },

  // ═══════════════════════════════════════
  // FORD
  // ═══════════════════════════════════════
  {
    id: 'fusion-2',
    make: 'Ford',
    model: 'Fusion',
    yearRange: '2018-2020',
    auction: [1800, 5000],
    type: 'Sedán',
    engine: '2.5L 4cyl',
    description: 'Sedán mediano elegante con opciones híbridas y AWD.',
    trims: [
      {
        name: 'S',
        auction: [1800, 2600],
        features: ['Motor 2.5L', 'Sync 3', 'Cámara reversa'],
      },
      {
        name: 'SE',
        auction: [2400, 3500],
        features: ['Motor 1.5T', 'Rines 17"', 'CarPlay'],
      },
      {
        name: 'Titanium',
        auction: [3200, 4200],
        features: ['Motor 2.0T', 'Cuero', 'Sunroof'],
      },
      {
        name: 'Sport',
        auction: [3800, 5000],
        features: ['Motor 2.7T V6', 'AWD', 'Rines 19"'],
      },
    ],
  },
  {
    id: 'ecosport-2',
    make: 'Ford',
    model: 'EcoSport',
    yearRange: '2018-2022',
    auction: [1800, 4500],
    type: 'SUV',
    engine: '2.0L 4cyl',
    description: 'SUV subcompacta urbana. Compacta y fácil de estacionar.',
    trims: [
      {
        name: 'S',
        auction: [1800, 2500],
        features: ['Motor 1.0T', 'Sync 3', 'Cámara reversa'],
      },
      {
        name: 'SE',
        auction: [2300, 3200],
        features: ['Motor 2.0L', 'Rines 16"', 'CarPlay'],
      },
      {
        name: 'Titanium',
        auction: [3000, 3800],
        features: ['Cuero parcial', 'Sunroof', 'B&O Audio'],
      },
      {
        name: 'SES',
        auction: [3500, 4500],
        features: ['Motor 2.0L AWD', 'Rines 17"', 'Interior deportivo'],
      },
    ],
  },
  {
    id: 'escape-3',
    make: 'Ford',
    model: 'Escape',
    yearRange: '2018-2019',
    auction: [2200, 5000],
    type: 'SUV',
    engine: '1.5L 4cyl Turbo',
    description: 'SUV compacta popular con opciones de motor turbo.',
    trims: [
      {
        name: 'S',
        auction: [2200, 3000],
        features: ['Motor 2.5L', 'Sync 3', 'Cámara reversa'],
      },
      {
        name: 'SE',
        auction: [2800, 3600],
        features: ['Motor 1.5T', 'Rines 17"', 'CarPlay'],
      },
      {
        name: 'SEL',
        auction: [3400, 4200],
        features: ['Cuero parcial', 'Sunroof', 'Keyless'],
      },
      {
        name: 'Titanium',
        auction: [4000, 5000],
        features: ['Motor 2.0T', 'Cuero', 'B&O Audio'],
      },
    ],
  },
  {
    id: 'escape-4',
    make: 'Ford',
    model: 'Escape',
    yearRange: '2020-2024',
    auction: [4500, 10500],
    type: 'SUV',
    engine: '1.5L 3cyl Turbo',
    description: 'SUV compacta renovada con opciones híbridas y plug-in.',
    trims: [
      {
        name: 'S',
        auction: [4500, 6000],
        features: ['Motor 1.5T', 'Pantalla 8"', 'Co-Pilot360'],
      },
      {
        name: 'SE',
        auction: [5500, 7500],
        features: ['Rines 17"', 'Asientos calefactados', 'Keyless'],
      },
      {
        name: 'SEL',
        auction: [7000, 9000],
        features: ['Cuero parcial', 'Pantalla 13.2"', 'B&O Audio'],
      },
      {
        name: 'Titanium',
        auction: [8500, 10500],
        features: ['Cuero', 'Techo panorámico', 'Head-up Display'],
      },
    ],
  },
  {
    id: 'edge-2',
    make: 'Ford',
    model: 'Edge',
    yearRange: '2018-2024',
    auction: [4000, 11500],
    type: 'SUV',
    engine: '2.0L 4cyl Turbo',
    description: 'SUV mediana con buen espacio y opciones deportivas.',
    trims: [
      {
        name: 'SE',
        auction: [4000, 6000],
        features: ['Motor 2.0T', 'Sync 3', 'Co-Pilot360'],
      },
      {
        name: 'SEL',
        auction: [5500, 7500],
        features: ['Cuero parcial', 'Rines 18"', 'Asientos calefactados'],
      },
      {
        name: 'Titanium',
        auction: [7000, 9500],
        features: ['Cuero', 'Sunroof', 'B&O Audio'],
      },
      {
        name: 'ST',
        auction: [9000, 11500],
        features: ['Motor 2.7T V6', 'Rines 21"', 'Suspensión deportiva'],
      },
    ],
  },
  {
    id: 'broncosport-1',
    make: 'Ford',
    model: 'Bronco Sport',
    yearRange: '2021-2024',
    auction: [7000, 13500],
    type: 'SUV',
    engine: '1.5L 3cyl Turbo',
    description: 'SUV compacta aventurera con capacidad off-road real.',
    trims: [
      {
        name: 'Base',
        auction: [7000, 8500],
        features: ['Motor 1.5T', 'Pantalla 8"', 'Co-Pilot360'],
      },
      {
        name: 'Big Bend',
        auction: [8500, 10000],
        features: ['Rines 17"', 'Asientos calefactados', 'Keyless'],
      },
      {
        name: 'Outer Banks',
        auction: [10000, 12000],
        features: ['Cuero', 'Rines 18"', 'B&O Audio'],
      },
      {
        name: 'Badlands',
        auction: [11500, 13500],
        features: ['Motor 2.0T', 'Trail Control', 'Diferencial trasero'],
      },
    ],
  },
  {
    id: 'explorer-6',
    make: 'Ford',
    model: 'Explorer',
    yearRange: '2020-2024',
    auction: [8000, 18000],
    type: 'SUV',
    engine: '2.3L 4cyl Turbo',
    description: 'SUV grande con 3 filas, tracción trasera y motor potente.',
    trims: [
      {
        name: 'Base',
        auction: [8000, 11000],
        features: ['Motor 2.3T', 'Pantalla 8"', 'Co-Pilot360'],
      },
      {
        name: 'XLT',
        auction: [10500, 13000],
        features: ['Rines 20"', 'Asientos calefactados', 'Keyless'],
      },
      {
        name: 'Limited',
        auction: [12500, 15500],
        features: ['Cuero', 'B&O Audio', 'Techo panorámico'],
      },
      {
        name: 'ST',
        auction: [15000, 18000],
        features: ['Motor 3.0T V6', 'Rines 21"', 'Suspensión adaptativa'],
      },
    ],
  },
  {
    id: 'ranger-3',
    make: 'Ford',
    model: 'Ranger',
    yearRange: '2019-2024',
    auction: [6000, 15000],
    type: 'Pick-up',
    engine: '2.3L 4cyl Turbo',
    description: 'Pick-up mediana con motor EcoBoost y capacidad off-road.',
    trims: [
      {
        name: 'XL',
        auction: [6000, 8500],
        features: ['Motor 2.3T', 'Pantalla 8"', 'Co-Pilot360'],
      },
      {
        name: 'XLT',
        auction: [8000, 10500],
        features: ['Rines 17"', 'CarPlay', 'Asientos calefactados'],
      },
      {
        name: 'Lariat',
        auction: [10000, 13000],
        features: ['Cuero', 'B&O Audio', 'Navegación'],
      },
      {
        name: 'Tremor',
        auction: [12000, 15000],
        features: ['Fox shocks', 'Rines 17" off-road', 'Trail Control'],
      },
    ],
  },
  {
    id: 'f150-13',
    make: 'Ford',
    model: 'F-150',
    yearRange: '2018-2020',
    auction: [7000, 15000],
    type: 'Pick-up',
    engine: '3.5L V6 Turbo',
    description: 'Cuerpo de aluminio, motores EcoBoost potentes.',
    trims: [
      {
        name: 'XL',
        auction: [7000, 9500],
        features: ['Básico', 'V6 o V8', 'Cámara reversa'],
      },
      {
        name: 'XLT',
        auction: [9000, 12000],
        features: ['Sync 3', 'Cromo', 'Asientos tela premium'],
      },
      {
        name: 'Lariat',
        auction: [11500, 15000],
        features: ['Cuero', 'Ventilados', 'Pantalla 8"'],
      },
    ],
  },
  {
    id: 'f150-14',
    make: 'Ford',
    model: 'F-150',
    yearRange: '2021-2024',
    auction: [14000, 26000],
    type: 'Pick-up',
    engine: '3.5L V6 Turbo',
    description: 'La más tecnológica. Pantallas enormes y opción híbrida.',
    trims: [
      {
        name: 'XL',
        auction: [14000, 17000],
        features: ['Pantalla 8"', 'Safety Tech', 'CarPlay'],
      },
      {
        name: 'XLT',
        auction: [17000, 21000],
        features: ['Pantalla 12" (opc)', 'Pro Power', '360 Camera'],
      },
      {
        name: 'Lariat',
        auction: [21000, 26000],
        features: ['Pantalla 12" std', 'Cuero', 'Digital Cluster'],
      },
    ],
  },
  {
    id: 'mustang-6',
    make: 'Ford',
    model: 'Mustang',
    yearRange: '2018-2024',
    auction: [5500, 18000],
    type: 'Deportivo',
    engine: '2.3L 4cyl Turbo',
    description: 'El muscle car americano por excelencia. Potencia y estilo.',
    trims: [
      {
        name: 'EcoBoost',
        auction: [5500, 8000],
        features: ['Motor 2.3T 310hp', 'Pantalla 8"', 'Rines 17"'],
      },
      {
        name: 'EcoBoost Premium',
        auction: [7500, 11000],
        features: ['Cuero', 'B&O Audio', 'Digital Cluster'],
      },
      {
        name: 'GT',
        auction: [10000, 14000],
        features: ['Motor 5.0L V8 460hp', 'Rines 18"', 'Modo Track'],
      },
      {
        name: 'GT Premium',
        auction: [13000, 18000],
        features: ['Cuero', 'Recaro (opc)', 'MagneRide'],
      },
    ],
  },

  // ═══════════════════════════════════════
  // CHEVROLET
  // ═══════════════════════════════════════
  {
    id: 'spark-4',
    make: 'Chevrolet',
    model: 'Spark',
    yearRange: '2018-2022',
    auction: [400, 1300],
    type: 'Hatchback',
    engine: '1.4L 4cyl',
    description: 'El auto más económico. Ideal para ciudad y primer auto.',
    trims: [
      {
        name: 'LS',
        auction: [400, 700],
        features: ['Motor 1.4L', 'Pantalla 7"', 'CarPlay'],
      },
      {
        name: 'LT',
        auction: [600, 1000],
        features: ['Rines 15"', 'Cámara reversa', 'Keyless'],
      },
      {
        name: 'ACTIV',
        auction: [900, 1300],
        features: ['Estilo crossover', 'Rieles techo', 'Interior especial'],
      },
    ],
  },
  {
    id: 'cruze-2',
    make: 'Chevrolet',
    model: 'Cruze',
    yearRange: '2018-2019',
    auction: [1000, 2500],
    type: 'Sedán',
    engine: '1.4L 4cyl Turbo',
    description: 'Compacto económico con motor turbo eficiente.',
    trims: [
      {
        name: 'L',
        auction: [1000, 1400],
        features: ['Motor 1.4T', 'Pantalla 7"', 'CarPlay'],
      },
      {
        name: 'LS',
        auction: [1300, 1700],
        features: ['Rines 16"', 'Keyless', 'Cámara reversa'],
      },
      {
        name: 'LT',
        auction: [1600, 2100],
        features: ['Rines 17"', 'Asiento eléctrico', 'Climatizador'],
      },
      {
        name: 'Premier',
        auction: [1900, 2500],
        features: ['Cuero', 'Asientos calefactados', 'Bose'],
      },
    ],
  },
  {
    id: 'malibu-9',
    make: 'Chevrolet',
    model: 'Malibu',
    yearRange: '2018-2024',
    auction: [2000, 7000],
    type: 'Sedán',
    engine: '1.5L 4cyl Turbo',
    description: 'Sedán mediano espacioso con buen equipamiento.',
    trims: [
      {
        name: 'L',
        auction: [2000, 3200],
        features: ['Motor 1.5T', 'Pantalla 7"', 'CarPlay'],
      },
      {
        name: 'LS',
        auction: [3000, 4200],
        features: ['Rines 16"', 'Keyless', 'Teen Driver'],
      },
      {
        name: 'RS',
        auction: [4000, 5500],
        features: ['Rines negros 18"', 'Spoiler', 'Interior deportivo'],
      },
      {
        name: 'Premier',
        auction: [5500, 7000],
        features: ['Cuero', 'Bose', 'Asientos ventilados'],
      },
    ],
  },
  {
    id: 'camaro-6',
    make: 'Chevrolet',
    model: 'Camaro',
    yearRange: '2018-2024',
    auction: [5500, 18000],
    type: 'Deportivo',
    engine: '2.0L 4cyl Turbo',
    description: 'Muscle car americano. Potencia bruta y manejo preciso.',
    trims: [
      {
        name: '1LT',
        auction: [5500, 8000],
        features: ['Motor 2.0T 275hp', 'Pantalla 8"', 'Rines 18"'],
      },
      {
        name: '1SS',
        auction: [9000, 12000],
        features: ['Motor 6.2L V8 455hp', 'Brembo', 'Modo Track'],
      },
      {
        name: '2SS',
        auction: [11000, 14000],
        features: ['Motor 6.2L V8', 'Head-up Display', 'Recaro opc'],
      },
      {
        name: 'ZL1',
        auction: [14500, 18000],
        features: ['Motor 6.2L SC 650hp', 'MagneRide', 'Alerón carbono'],
      },
    ],
  },
  {
    id: 'trailblazer-3',
    make: 'Chevrolet',
    model: 'Trailblazer',
    yearRange: '2021-2024',
    auction: [4000, 8000],
    type: 'SUV',
    engine: '1.3L 3cyl Turbo',
    description: 'SUV subcompacta con diseño moderno y buen equipamiento.',
    trims: [
      {
        name: 'L',
        auction: [4000, 5000],
        features: ['Motor 1.2T', 'Pantalla 7"', 'CarPlay'],
      },
      {
        name: 'LS',
        auction: [4800, 5800],
        features: ['Rines 17"', 'Cámara reversa', 'Keyless'],
      },
      {
        name: 'LT',
        auction: [5500, 6800],
        features: ['Rines 18"', 'Asientos calefactados', 'Blind Spot'],
      },
      {
        name: 'RS',
        auction: [6500, 8000],
        features: ['Rines negros 18"', 'Interior deportivo', 'Bose'],
      },
    ],
  },
  {
    id: 'trax-2',
    make: 'Chevrolet',
    model: 'Trax',
    yearRange: '2024',
    auction: [5500, 8500],
    type: 'SUV',
    engine: '1.2L 3cyl Turbo',
    description: 'Totalmente rediseñada. Mucho más grande y moderna.',
    trims: [
      {
        name: 'LS',
        auction: [5500, 6500],
        features: ['Motor 1.2T', 'Pantalla 8"', 'CarPlay'],
      },
      {
        name: 'LT',
        auction: [6200, 7200],
        features: ['Rines 18"', 'Asiento eléctrico', 'Blind Spot'],
      },
      {
        name: 'RS',
        auction: [7000, 8000],
        features: ['Rines negros 19"', 'Interior deportivo', 'Bose'],
      },
      {
        name: 'Activ',
        auction: [7500, 8500],
        features: ['Estilo off-road', 'Rieles techo', 'Protectores'],
      },
    ],
  },
  {
    id: 'equinox-3',
    make: 'Chevrolet',
    model: 'Equinox',
    yearRange: '2018-2024',
    auction: [3000, 9000],
    type: 'SUV',
    engine: '1.5L 4cyl Turbo',
    description: 'SUV compacta versátil con buen espacio y tecnología.',
    trims: [
      {
        name: 'L',
        auction: [3000, 4500],
        features: ['Motor 1.5T', 'Pantalla 7"', 'CarPlay'],
      },
      {
        name: 'LS',
        auction: [4000, 5500],
        features: ['Rines 17"', 'Safety Tech', 'Keyless'],
      },
      {
        name: 'LT',
        auction: [5200, 7000],
        features: ['Rines 18"', 'Power Liftgate', 'Asientos calefactados'],
      },
      {
        name: 'Premier',
        auction: [7000, 9000],
        features: ['Cuero', 'Bose', 'Techo panorámico'],
      },
    ],
  },
  {
    id: 'blazer-3',
    make: 'Chevrolet',
    model: 'Blazer',
    yearRange: '2019-2024',
    auction: [6000, 14500],
    type: 'SUV',
    engine: '2.0L 4cyl Turbo',
    description: 'SUV mediana con diseño Camaro-inspired y motor potente.',
    trims: [
      {
        name: 'L',
        auction: [6000, 8000],
        features: ['Motor 2.0T', 'Pantalla 8"', 'Safety Tech'],
      },
      {
        name: 'LT',
        auction: [7500, 10000],
        features: ['Rines 18"', 'Asientos calefactados', 'Keyless'],
      },
      {
        name: 'RS',
        auction: [9500, 12000],
        features: ['Rines negros 21"', 'Interior deportivo', 'Bose'],
      },
      {
        name: 'Premier',
        auction: [11500, 14500],
        features: ['Motor 3.6L V6', 'Cuero', 'Techo panorámico'],
      },
    ],
  },
  {
    id: 'traverse-2',
    make: 'Chevrolet',
    model: 'Traverse',
    yearRange: '2018-2024',
    auction: [5500, 13500],
    type: 'SUV',
    engine: '3.6L V6',
    description: 'SUV grande de 3 filas con mucho espacio para familias.',
    trims: [
      {
        name: 'L',
        auction: [5500, 7500],
        features: ['Motor 3.6L V6', 'Pantalla 7"', 'CarPlay'],
      },
      {
        name: 'LS',
        auction: [7000, 9000],
        features: ['Rines 18"', 'Cámara reversa', 'Keyless'],
      },
      {
        name: 'LT',
        auction: [8500, 11000],
        features: ['Cuero parcial', 'Bose', 'Power Liftgate'],
      },
      {
        name: 'Premier/High Country',
        auction: [10500, 13500],
        features: ['Cuero', 'Techo panorámico', 'Cámaras 360'],
      },
    ],
  },
  {
    id: 'colorado-3',
    make: 'Chevrolet',
    model: 'Colorado',
    yearRange: '2018-2024',
    auction: [5500, 15000],
    type: 'Pick-up',
    engine: '3.6L V6',
    description: 'Pick-up mediana versátil con opciones diesel y off-road.',
    trims: [
      {
        name: 'WT',
        auction: [5500, 7500],
        features: ['Motor 2.5L', 'Pantalla 7"', 'CarPlay'],
      },
      {
        name: 'LT',
        auction: [7000, 10000],
        features: ['Motor 3.6L V6', 'Rines 17"', 'Asientos calefactados'],
      },
      {
        name: 'Z71',
        auction: [9500, 12500],
        features: ['Off-road pkg', 'Rines 17"', 'Skid plates'],
      },
      {
        name: 'ZR2',
        auction: [12000, 15000],
        features: ['Multimatic DSSV', 'Bloqueos', 'Rines 17" off-road'],
      },
    ],
  },
  {
    id: 'silverado-4',
    make: 'Chevrolet',
    model: 'Silverado 1500',
    yearRange: '2019-2024',
    auction: [9000, 22000],
    type: 'Pick-up',
    engine: '2.7L 4cyl Turbo',
    description: 'Pick-up full-size con opciones de motor V6, V8 y diesel.',
    trims: [
      {
        name: 'WT',
        auction: [9000, 12000],
        features: ['Motor 2.7T', 'Pantalla 7"', 'CarPlay'],
      },
      {
        name: 'Custom',
        auction: [11500, 15000],
        features: ['Rines 20"', 'LEDs', 'Carrocería color'],
      },
      {
        name: 'LT',
        auction: [14000, 18000],
        features: ['Rines cromados', 'Asientos calefactados', 'Trailering'],
      },
      {
        name: 'LTZ',
        auction: [17500, 22000],
        features: ['Cuero', 'Bose', 'Techo panorámico'],
      },
    ],
  },

  // ═══════════════════════════════════════
  // VOLKSWAGEN
  // ═══════════════════════════════════════
  {
    id: 'jetta-7',
    make: 'Volkswagen',
    model: 'Jetta',
    yearRange: '2019-2024',
    auction: [2500, 7000],
    type: 'Sedán',
    engine: '1.5L 4cyl Turbo',
    description: 'Ingeniería alemana accesible. Sólido, refinado y eficiente.',
    trims: [
      {
        name: 'S',
        auction: [2500, 3800],
        features: ['Motor 1.5T', 'Pantalla 6.5"', 'CarPlay'],
      },
      {
        name: 'SE',
        auction: [3500, 5000],
        features: ['Pantalla 8"', 'Sunroof', 'Asientos calefactados'],
      },
      {
        name: 'SEL',
        auction: [4500, 6000],
        features: ['Cuero', 'Digital Cockpit', 'Beats Audio'],
      },
      {
        name: 'GLI',
        auction: [5500, 7000],
        features: ['Motor 2.0T', 'Suspensión deportiva', 'Rines 18"'],
      },
    ],
  },
  {
    id: 'passat-8',
    make: 'Volkswagen',
    model: 'Passat',
    yearRange: '2018-2022',
    auction: [2000, 6000],
    type: 'Sedán',
    engine: '2.0L 4cyl Turbo',
    description: 'Sedán mediano espacioso con confort y calidad alemana.',
    trims: [
      {
        name: 'S',
        auction: [2000, 3000],
        features: ['Motor 2.0T', 'Pantalla 6.3"', 'CarPlay'],
      },
      {
        name: 'SE',
        auction: [2800, 4000],
        features: ['Pantalla 8"', 'Sunroof', 'Asientos calefactados'],
      },
      {
        name: 'SEL',
        auction: [3800, 5000],
        features: ['Cuero', 'Digital Cockpit', 'Fender Audio'],
      },
      {
        name: 'SEL Premium',
        auction: [4800, 6000],
        features: ['Ventilados', 'Cámaras 360', 'Head-up Display'],
      },
    ],
  },
  {
    id: 'taos-1',
    make: 'Volkswagen',
    model: 'Taos',
    yearRange: '2022-2024',
    auction: [5500, 10000],
    type: 'SUV',
    engine: '1.5L 4cyl Turbo',
    description: 'SUV subcompacta nueva. Espaciosa para su segmento.',
    trims: [
      {
        name: 'S',
        auction: [5500, 7000],
        features: ['Motor 1.5T', 'Pantalla 6.5"', 'Blind Spot'],
      },
      {
        name: 'SE',
        auction: [6800, 8500],
        features: ['Pantalla 8"', 'Sunroof', 'Asientos calefactados'],
      },
      {
        name: 'SEL',
        auction: [8000, 10000],
        features: ['Cuero', 'Digital Cockpit', 'Beats Audio'],
      },
    ],
  },
  {
    id: 'tiguan-2',
    make: 'Volkswagen',
    model: 'Tiguan',
    yearRange: '2018-2024',
    auction: [3500, 10000],
    type: 'SUV',
    engine: '2.0L 4cyl Turbo',
    description: 'SUV compacta alemana con opción de 3 filas y manejo europeo.',
    trims: [
      {
        name: 'S',
        auction: [3500, 5500],
        features: ['Motor 2.0T', 'Pantalla 6.5"', 'Blind Spot'],
      },
      {
        name: 'SE',
        auction: [5000, 7000],
        features: ['Pantalla 8"', 'Sunroof', 'Asientos calefactados'],
      },
      {
        name: 'SEL',
        auction: [6500, 8500],
        features: ['Cuero', 'Digital Cockpit', 'Fender Audio'],
      },
      {
        name: 'SEL Premium',
        auction: [8000, 10000],
        features: ['Rines 20"', 'Asientos ventilados', 'Head-up Display'],
      },
    ],
  },
  {
    id: 'atlas-1',
    make: 'Volkswagen',
    model: 'Atlas',
    yearRange: '2018-2024',
    auction: [5500, 13500],
    type: 'SUV',
    engine: '2.0L 4cyl Turbo',
    description: 'SUV grande con 3 filas reales. Mucho espacio para familias.',
    trims: [
      {
        name: 'S',
        auction: [5500, 7500],
        features: ['Motor 2.0T', 'Pantalla 6.5"', 'Blind Spot'],
      },
      {
        name: 'SE',
        auction: [7500, 10000],
        features: ['Pantalla 8"', 'Sunroof', 'Asientos calefactados'],
      },
      {
        name: 'SEL',
        auction: [9500, 12000],
        features: ['Cuero', 'Digital Cockpit', 'Fender Audio'],
      },
      {
        name: 'SEL Premium',
        auction: [11500, 13500],
        features: ['Motor V6', 'Asientos ventilados', 'Cámaras 360'],
      },
    ],
  },
  {
    id: 'atlascross-1',
    make: 'Volkswagen',
    model: 'Atlas Cross Sport',
    yearRange: '2020-2024',
    auction: [6000, 13500],
    type: 'SUV',
    engine: '2.0L 4cyl Turbo',
    description: 'Atlas en formato coupé. Más deportivo, 2 filas.',
    trims: [
      {
        name: 'S',
        auction: [6000, 8000],
        features: ['Motor 2.0T', 'Pantalla 6.5"', 'Blind Spot'],
      },
      {
        name: 'SE',
        auction: [7500, 10000],
        features: ['Pantalla 8"', 'Sunroof', 'Asientos calefactados'],
      },
      {
        name: 'SEL',
        auction: [9500, 12000],
        features: ['Cuero', 'Digital Cockpit', 'Fender Audio'],
      },
      {
        name: 'SEL Premium',
        auction: [11500, 13500],
        features: ['Motor V6', 'Ventilados', 'Cámaras 360'],
      },
    ],
  },
  {
    id: 'golf-8',
    make: 'Volkswagen',
    model: 'Golf',
    yearRange: '2018-2024',
    auction: [3000, 12000],
    type: 'Hatchback',
    engine: '1.4L 4cyl Turbo',
    description:
      'El hatchback por excelencia. Versátil, divertido y bien construido.',
    trims: [
      {
        name: 'TSI',
        auction: [3000, 5000],
        features: ['Motor 1.4T', 'Pantalla 8"', 'CarPlay'],
      },
      {
        name: 'SE',
        auction: [4500, 6500],
        features: ['Sunroof', 'Cuero', 'Digital Cockpit'],
      },
      {
        name: 'GTI S',
        auction: [6000, 9000],
        features: ['Motor 2.0T', 'Rines 18"', 'Diferencial LSD'],
      },
      {
        name: 'GTI Autobahn',
        auction: [8500, 12000],
        features: ['Cuero Clark Plaid', 'Fender Audio', 'DCC'],
      },
    ],
  },

  // ═══════════════════════════════════════
  // BMW
  // ═══════════════════════════════════════
  {
    id: 'bmw-2-f22',
    make: 'BMW',
    model: '2 Series',
    yearRange: '2018-2024',
    auction: [5500, 13500],
    type: 'Deportivo',
    engine: '2.0L 4cyl Turbo',
    description:
      'Coupé compacto deportivo. Manejo puro BMW en formato pequeño.',
    trims: [
      {
        name: '230i',
        auction: [5500, 8500],
        features: ['Motor 2.0T', 'iDrive', 'Asientos deportivos'],
      },
      {
        name: '230i xDrive',
        auction: [7000, 10000],
        features: ['AWD', 'Rines 18"', 'Asientos calefactados'],
      },
      {
        name: 'M240i',
        auction: [10000, 13500],
        features: ['Motor 3.0T', 'M Sport', 'Diferencial M'],
      },
    ],
  },
  {
    id: 'bmw-3-g20',
    make: 'BMW',
    model: '3 Series',
    yearRange: '2019-2024',
    auction: [8500, 20000],
    type: 'Deportivo',
    engine: '2.0L 4cyl Turbo',
    description:
      'El sedán deportivo de referencia. Manejo puro, tecnología premium.',
    trims: [
      {
        name: '330i',
        auction: [8500, 12000],
        features: ['Motor 2.0T 255hp', 'iDrive 7', 'Asientos deportivos'],
      },
      {
        name: '330i xDrive',
        auction: [10000, 14000],
        features: ['AWD', 'Rines 18"', 'Asientos calefactados'],
      },
      {
        name: 'M340i',
        auction: [14000, 17500],
        features: ['Motor 3.0T 382hp', 'M Sport', 'Diferencial M'],
      },
      {
        name: 'M340i xDrive',
        auction: [16500, 20000],
        features: ['AWD', 'Frenos M', 'Escape M Sport'],
      },
    ],
  },
  {
    id: 'bmw-4-g22',
    make: 'BMW',
    model: '4 Series',
    yearRange: '2021-2024',
    auction: [11500, 21000],
    type: 'Deportivo',
    engine: '2.0L 4cyl Turbo',
    description: 'Coupé/Gran Coupé elegante con diseño audaz y motor potente.',
    trims: [
      {
        name: '430i',
        auction: [11500, 14500],
        features: ['Motor 2.0T', 'iDrive 7', 'Cuero SensaTec'],
      },
      {
        name: '430i xDrive',
        auction: [13500, 17000],
        features: ['AWD', 'Rines 19"', 'Asientos calefactados'],
      },
      {
        name: 'M440i xDrive',
        auction: [17000, 21000],
        features: ['Motor 3.0T 382hp', 'M Sport', 'Harman Kardon'],
      },
    ],
  },
  {
    id: 'bmw-5-g30',
    make: 'BMW',
    model: '5 Series',
    yearRange: '2018-2024',
    auction: [8000, 21000],
    type: 'Sedán',
    engine: '2.0L 4cyl Turbo',
    description:
      'Sedán ejecutivo de lujo. Confort, tecnología y manejo deportivo.',
    trims: [
      {
        name: '530i',
        auction: [8000, 12000],
        features: ['Motor 2.0T', 'iDrive', 'Cuero'],
      },
      {
        name: '530i xDrive',
        auction: [10000, 14500],
        features: ['AWD', 'Rines 18"', 'Asientos calefactados'],
      },
      {
        name: '540i',
        auction: [13000, 17500],
        features: ['Motor 3.0T', 'M Sport', 'Harman Kardon'],
      },
      {
        name: 'M550i',
        auction: [17000, 21000],
        features: ['Motor 4.4T V8', 'M Sport Pro', 'Frenos M'],
      },
    ],
  },
  {
    id: 'bmw-x1-f48',
    make: 'BMW',
    model: 'X1',
    yearRange: '2018-2022',
    auction: [5500, 11500],
    type: 'SUV',
    engine: '2.0L 4cyl Turbo',
    description: 'SUV compacta premium. Entrada al mundo BMW con estilo.',
    trims: [
      {
        name: 'sDrive28i',
        auction: [5500, 7500],
        features: ['Motor 2.0T', 'Pantalla 6.5"', 'Cuero SensaTec'],
      },
      {
        name: 'xDrive28i',
        auction: [7000, 9500],
        features: ['AWD', 'Rines 18"', 'Asientos calefactados'],
      },
      {
        name: 'M35i',
        auction: [9000, 11500],
        features: ['Motor 2.0T 302hp', 'M Sport', 'Escape M'],
      },
    ],
  },
  {
    id: 'bmw-x1-u11',
    make: 'BMW',
    model: 'X1',
    yearRange: '2023-2024',
    auction: [11500, 18500],
    type: 'SUV',
    engine: '2.0L 4cyl Turbo',
    description:
      'Nueva generación totalmente rediseñada. Más grande y tecnológica.',
    trims: [
      {
        name: 'xDrive28i',
        auction: [11500, 14500],
        features: ['Motor 2.0T', 'iDrive 8', 'Curved Display'],
      },
      {
        name: 'M35i xDrive',
        auction: [15000, 18500],
        features: ['Motor 2.0T 312hp', 'M Sport', 'Harman Kardon'],
      },
    ],
  },
  {
    id: 'bmw-x3-g01',
    make: 'BMW',
    model: 'X3',
    yearRange: '2018-2024',
    auction: [8000, 18500],
    type: 'SUV',
    engine: '2.0L 4cyl Turbo',
    description:
      'SUV premium deportiva. Equilibrio perfecto entre lujo y manejo.',
    trims: [
      {
        name: 'sDrive30i',
        auction: [8000, 11000],
        features: ['Motor 2.0T', 'iDrive', 'Cuero SensaTec'],
      },
      {
        name: 'xDrive30i',
        auction: [10000, 14000],
        features: ['AWD', 'Rines 19"', 'Asientos calefactados'],
      },
      {
        name: 'M40i',
        auction: [14000, 18500],
        features: ['Motor 3.0T 382hp', 'M Sport', 'Harman Kardon'],
      },
    ],
  },
  {
    id: 'bmw-x5-g05',
    make: 'BMW',
    model: 'X5',
    yearRange: '2019-2024',
    auction: [15000, 27000],
    type: 'SUV',
    engine: '3.0L 6cyl Turbo',
    description: 'SUV grande de lujo. Potente, tecnológica y muy capaz.',
    trims: [
      {
        name: 'sDrive40i',
        auction: [15000, 18000],
        features: ['Motor 3.0T', 'iDrive 7', 'Cuero'],
      },
      {
        name: 'xDrive40i',
        auction: [18000, 22000],
        features: ['AWD', 'Rines 20"', 'Panoramic Sky Lounge'],
      },
      {
        name: 'M50i',
        auction: [22000, 27000],
        features: ['Motor 4.4T V8', 'M Sport', 'Bowers & Wilkins'],
      },
    ],
  },

  // ═══════════════════════════════════════
  // AUDI
  // ═══════════════════════════════════════
  {
    id: 'audi-a3-8v',
    make: 'Audi',
    model: 'A3',
    yearRange: '2018-2024',
    auction: [5500, 13500],
    type: 'Sedán',
    engine: '2.0L 4cyl Turbo',
    description: 'Sedán compacto premium. Lujo alemán en formato accesible.',
    trims: [
      {
        name: 'Premium',
        auction: [5500, 8000],
        features: ['Motor 2.0T', 'Virtual Cockpit', 'Cuero'],
      },
      {
        name: 'Premium Plus',
        auction: [7500, 10500],
        features: ['Audi Side Assist', 'Bang & Olufsen', 'Sunroof'],
      },
      {
        name: 'Prestige',
        auction: [10000, 13500],
        features: ['Head-up Display', 'Navegación', 'Matrix LED'],
      },
    ],
  },
  {
    id: 'audi-a4-b9',
    make: 'Audi',
    model: 'A4',
    yearRange: '2018-2024',
    auction: [7000, 16500],
    type: 'Sedán',
    engine: '2.0L 4cyl Turbo',
    description: 'Sedán de lujo con tecnología Virtual Cockpit y quattro AWD.',
    trims: [
      {
        name: 'Premium',
        auction: [7000, 10000],
        features: ['Motor 2.0T', 'Virtual Cockpit', 'Cuero'],
      },
      {
        name: 'Premium Plus',
        auction: [9500, 13000],
        features: ['Audi Side Assist', 'Bang & Olufsen', 'Sunroof'],
      },
      {
        name: 'Prestige',
        auction: [12500, 16500],
        features: ['Head-up Display', 'Cámaras 360', 'Navegación'],
      },
    ],
  },
  {
    id: 'audi-a5-f5',
    make: 'Audi',
    model: 'A5',
    yearRange: '2018-2024',
    auction: [8500, 18500],
    type: 'Deportivo',
    engine: '2.0L 4cyl Turbo',
    description:
      'Coupé/Sportback elegante con diseño atemporal y motor potente.',
    trims: [
      {
        name: 'Premium',
        auction: [8500, 12000],
        features: ['Motor 2.0T', 'Virtual Cockpit', 'Cuero'],
      },
      {
        name: 'Premium Plus',
        auction: [11500, 15000],
        features: ['Audi Side Assist', 'Bang & Olufsen', 'Sunroof'],
      },
      {
        name: 'Prestige',
        auction: [14500, 18500],
        features: ['Head-up Display', 'Cámaras 360', 'Matrix LED'],
      },
    ],
  },
  {
    id: 'audi-a6-c8',
    make: 'Audi',
    model: 'A6',
    yearRange: '2019-2024',
    auction: [11500, 21000],
    type: 'Sedán',
    engine: '2.0L 4cyl Turbo',
    description:
      'Sedán ejecutivo de lujo con tecnología dual-screen y quattro.',
    trims: [
      {
        name: 'Premium',
        auction: [11500, 14500],
        features: ['Motor 2.0T', 'Dual Screen', 'Cuero'],
      },
      {
        name: 'Premium Plus',
        auction: [14000, 17500],
        features: ['Audi Side Assist', 'Bang & Olufsen', 'Sunroof'],
      },
      {
        name: 'Prestige',
        auction: [17000, 21000],
        features: ['Head-up Display', 'Cámaras 360', 'Navegación'],
      },
    ],
  },
  {
    id: 'audi-q3-f3',
    make: 'Audi',
    model: 'Q3',
    yearRange: '2019-2024',
    auction: [7000, 13500],
    type: 'SUV',
    engine: '2.0L 4cyl Turbo',
    description:
      'SUV compacta premium. Entrada al mundo Audi con estilo urbano.',
    trims: [
      {
        name: 'Premium',
        auction: [7000, 9000],
        features: ['Motor 2.0T', 'quattro AWD', 'Pantalla 10.1"'],
      },
      {
        name: 'Premium Plus',
        auction: [9000, 11500],
        features: ['Audi Side Assist', 'Sunroof panorámico', 'Cuero'],
      },
      {
        name: 'Prestige',
        auction: [11000, 13500],
        features: ['Virtual Cockpit Plus', 'Bang & Olufsen', 'Navegación'],
      },
    ],
  },
  {
    id: 'audi-q5-fy',
    make: 'Audi',
    model: 'Q5',
    yearRange: '2018-2024',
    auction: [8500, 18500],
    type: 'SUV',
    engine: '2.0L 4cyl Turbo',
    description: 'SUV premium con quattro AWD de serie. Refinada y capaz.',
    trims: [
      {
        name: 'Premium',
        auction: [8500, 12000],
        features: ['Motor 2.0T', 'quattro AWD', 'Virtual Cockpit'],
      },
      {
        name: 'Premium Plus',
        auction: [11500, 15000],
        features: ['Audi Side Assist', 'Bang & Olufsen', 'Sunroof panorámico'],
      },
      {
        name: 'Prestige',
        auction: [14500, 18500],
        features: ['Head-up Display', 'Cámaras 360', 'Navegación'],
      },
    ],
  },
  {
    id: 'audi-q7-4m',
    make: 'Audi',
    model: 'Q7',
    yearRange: '2018-2024',
    auction: [10000, 21000],
    type: 'SUV',
    engine: '2.0L 4cyl Turbo',
    description: 'SUV grande de lujo con 3 filas y quattro AWD.',
    trims: [
      {
        name: 'Premium',
        auction: [10000, 13500],
        features: ['Motor 2.0T', 'quattro AWD', 'Virtual Cockpit'],
      },
      {
        name: 'Premium Plus',
        auction: [13000, 17000],
        features: ['Audi Side Assist', 'Bang & Olufsen', 'Sunroof panorámico'],
      },
      {
        name: 'Prestige',
        auction: [16500, 21000],
        features: ['Head-up Display', 'Cámaras 360', 'Asientos ventilados'],
      },
    ],
  },
  {
    id: 'audi-q8-4m',
    make: 'Audi',
    model: 'Q8',
    yearRange: '2019-2024',
    auction: [16000, 26000],
    type: 'SUV',
    engine: '3.0L V6 Turbo',
    description: 'SUV coupé flagship. Diseño imponente y tecnología de punta.',
    trims: [
      {
        name: 'Premium',
        auction: [16000, 19000],
        features: ['Motor 3.0T V6', 'quattro AWD', 'Dual Screen'],
      },
      {
        name: 'Premium Plus',
        auction: [19000, 22500],
        features: ['Bang & Olufsen', 'Sunroof panorámico', 'Matrix LED'],
      },
      {
        name: 'Prestige',
        auction: [22000, 26000],
        features: ['Head-up Display', 'Cámaras 360', 'Asientos masaje'],
      },
    ],
  },

  // ═══════════════════════════════════════
  // DEPORTIVOS ADICIONALES
  // ═══════════════════════════════════════
  {
    id: 'corvette-c7',
    make: 'Chevrolet',
    model: 'Corvette',
    yearRange: '2018-2019',
    auction: [18000, 32000],
    type: 'Deportivo',
    engine: '6.2L V8',
    description:
      'Deportivo americano icónico. Motor V8 frontal, rendimiento puro.',
    trims: [
      {
        name: '1LT',
        auction: [18000, 22000],
        features: ['Motor 6.2L V8 455hp', 'Rines 18/19"', 'Modo Track'],
      },
      {
        name: '2LT',
        auction: [21000, 25000],
        features: ['Cuero', 'Head-up Display', 'Bose'],
      },
      {
        name: '3LT',
        auction: [24000, 28000],
        features: ['Cuero Nappa', 'Navegación', 'Cámaras'],
      },
      {
        name: 'Z06',
        auction: [28000, 32000],
        features: ['Motor 6.2L SC 650hp', 'Aero pkg', 'MagneRide'],
      },
    ],
  },
  {
    id: 'corvette-c8',
    make: 'Chevrolet',
    model: 'Corvette',
    yearRange: '2020-2024',
    auction: [30000, 48000],
    type: 'Deportivo',
    engine: '6.2L V8',
    description: 'Motor central revolucionario. Rendimiento de superdeportivo.',
    trims: [
      {
        name: '1LT',
        auction: [30000, 35000],
        features: ['Motor 6.2L V8 490hp', 'Motor central', 'Modo Track'],
      },
      {
        name: '2LT',
        auction: [34000, 40000],
        features: ['Cuero GT2', 'Head-up Display', 'Bose'],
      },
      {
        name: '3LT',
        auction: [38000, 44000],
        features: ['Cuero Nappa', 'Suede microfiber', 'Cámaras'],
      },
      {
        name: 'Z06',
        auction: [44000, 48000],
        features: ['Motor 5.5L V8 670hp', 'Aero pkg', 'Carbono'],
      },
    ],
  },
  {
    id: 'challenger-3',
    make: 'Dodge',
    model: 'Challenger',
    yearRange: '2018-2023',
    auction: [6000, 22000],
    type: 'Deportivo',
    engine: '3.6L V6',
    description: 'Muscle car retro con opciones de motor V6 hasta Hellcat.',
    trims: [
      {
        name: 'SXT',
        auction: [6000, 8500],
        features: ['Motor 3.6L V6 303hp', 'Pantalla 7"', 'Rines 18"'],
      },
      {
        name: 'R/T',
        auction: [9000, 12000],
        features: ['Motor 5.7L HEMI V8 375hp', 'Escape activo', 'Rines 20"'],
      },
      {
        name: 'R/T Scat Pack',
        auction: [12000, 16000],
        features: ['Motor 6.4L V8 485hp', 'Brembo', 'Launch Control'],
      },
      {
        name: 'SRT Hellcat',
        auction: [18000, 22000],
        features: ['Motor 6.2L SC 717hp', 'Bilstein', 'SRT Drive Modes'],
      },
    ],
  },
  {
    id: 'charger-7',
    make: 'Dodge',
    model: 'Charger',
    yearRange: '2018-2023',
    auction: [5500, 20000],
    type: 'Deportivo',
    engine: '3.6L V6',
    description: 'Sedán muscle car de 4 puertas. Potencia y practicidad.',
    trims: [
      {
        name: 'SXT',
        auction: [5500, 8000],
        features: ['Motor 3.6L V6 300hp', 'Pantalla 8.4"', 'Rines 17"'],
      },
      {
        name: 'R/T',
        auction: [8500, 11500],
        features: ['Motor 5.7L HEMI V8 370hp', 'Escape activo', 'Rines 20"'],
      },
      {
        name: 'Scat Pack',
        auction: [11500, 15000],
        features: ['Motor 6.4L V8 485hp', 'Brembo', 'Launch Control'],
      },
      {
        name: 'SRT Hellcat',
        auction: [17000, 20000],
        features: ['Motor 6.2L SC 717hp', 'Bilstein', 'SRT Modes'],
      },
    ],
  },
  {
    id: 'brz-2',
    make: 'Subaru',
    model: 'BRZ',
    yearRange: '2022-2024',
    auction: [10000, 15000],
    type: 'Deportivo',
    engine: '2.4L 4cyl',
    description: 'Coupé deportivo ligero con motor boxer. Manejo puro.',
    trims: [
      {
        name: 'Premium',
        auction: [10000, 12000],
        features: ['Motor 2.4L 228hp', 'Pantalla 8"', 'Rines 17"'],
      },
      {
        name: 'Limited',
        auction: [12500, 15000],
        features: ['Cuero/Alcantara', 'Rines 18"', 'Blind Spot'],
      },
    ],
  },
  {
    id: 'wrx-5',
    make: 'Subaru',
    model: 'WRX',
    yearRange: '2022-2024',
    auction: [10000, 16000],
    type: 'Deportivo',
    engine: '2.4L 4cyl Turbo',
    description: 'Sedán deportivo AWD con motor turbo. Rally en la calle.',
    trims: [
      {
        name: 'Base',
        auction: [10000, 12000],
        features: ['Motor 2.4T 271hp', 'AWD', 'Pantalla 11.6"'],
      },
      {
        name: 'Premium',
        auction: [11500, 13500],
        features: ['Sunroof', 'Recaro opc', 'Rines 18"'],
      },
      {
        name: 'GT',
        auction: [13500, 16000],
        features: ['Cuero', 'Harman Kardon', 'Suspensión adaptativa'],
      },
    ],
  },
  {
    id: '370z',
    make: 'Nissan',
    model: '370Z',
    yearRange: '2018-2020',
    auction: [7000, 14000],
    type: 'Deportivo',
    engine: '3.7L V6',
    description:
      'Coupé deportivo japonés clásico. Motor V6 aspirado y manejo puro.',
    trims: [
      {
        name: 'Base',
        auction: [7000, 9000],
        features: ['Motor 3.7L V6 332hp', 'Rines 18"', 'Pantalla 7"'],
      },
      {
        name: 'Sport',
        auction: [8500, 11000],
        features: ['Syncro Rev Match', 'Rines 19"', 'Viscous LSD'],
      },
      {
        name: 'Sport Touring',
        auction: [10500, 14000],
        features: ['Cuero', 'Navegación', 'Bose'],
      },
    ],
  },
  {
    id: 'nissan-z',
    make: 'Nissan',
    model: 'Z',
    yearRange: '2023-2024',
    auction: [16000, 22000],
    type: 'Deportivo',
    engine: '3.0L V6 Twin Turbo',
    description: 'El nuevo Z. Motor twin-turbo V6 con diseño retro-moderno.',
    trims: [
      {
        name: 'Sport',
        auction: [16000, 18000],
        features: ['Motor 3.0TT V6 400hp', 'Pantalla 9"', 'Rines 18"'],
      },
      {
        name: 'Performance',
        auction: [18500, 22000],
        features: ['Rines 19"', 'LSD mecánico', 'Bose'],
      },
    ],
  },
  {
    id: 'gr86',
    make: 'Toyota',
    model: 'GR86',
    yearRange: '2022-2024',
    auction: [10000, 15000],
    type: 'Deportivo',
    engine: '2.4L 4cyl',
    description: 'Coupé deportivo ligero. Hermano del BRZ con alma Toyota.',
    trims: [
      {
        name: 'Base',
        auction: [10000, 12000],
        features: ['Motor 2.4L 228hp', 'Pantalla 8"', 'Rines 17"'],
      },
      {
        name: 'Premium',
        auction: [12500, 15000],
        features: ['Cuero/Alcantara', 'Rines 18"', 'Blind Spot'],
      },
    ],
  },
  {
    id: 'supra-5',
    make: 'Toyota',
    model: 'GR Supra',
    yearRange: '2020-2024',
    auction: [16000, 26000],
    type: 'Deportivo',
    engine: '3.0L 6cyl Turbo',
    description:
      'Leyenda japonesa renacida. Motor turbo BMW y chasis excepcional.',
    trims: [
      {
        name: '2.0',
        auction: [16000, 19000],
        features: ['Motor 2.0T 255hp', 'Pantalla 8.8"', 'Rines 18"'],
      },
      {
        name: '3.0',
        auction: [20000, 23000],
        features: ['Motor 3.0T 382hp', 'Diferencial activo', 'Rines 19"'],
      },
      {
        name: '3.0 Premium',
        auction: [22000, 26000],
        features: ['Cuero', 'JBL Audio', 'Head-up Display'],
      },
    ],
  },
]

// ═══════════════════════════════════════
// EXPORTED CAR DATA (computed from auction estimates)
// ═══════════════════════════════════════
export const carData: CarModel[] = buildCarData(rawCarData)
